import Phaser from 'phaser';
import { COLORS, HEX } from '../constants/colors';
import { Player } from '../entities/Player';
import { Bullet } from '../entities/Bullet';
import { Grenade, GRENADE_RADIUS } from '../entities/Grenade';
import { Beam } from '../entities/Beam';
import { EnemyProjectile } from '../entities/EnemyProjectile';
import { Zone } from '../entities/Zone';
import { Enemy } from '../entities/Enemy';
import { Boss } from '../entities/Boss';
import { MuzzleFlashFX } from '../entities/MuzzleFlashFX';
import { getEnemyConfig, ENEMY_CONFIGS } from '../constants/enemies';
import { getBossConfig, getWaveEventConfig } from '../constants/bosses';
import type { WaveEventConfig } from '../constants/bosses/WaveEventConfig';
import {
  getFieldTopY,
  getFieldBottomY,
  getFieldLeftX,
  getEnemyMovementTopY,
  getEnemyMovementBottomY,
} from '../constants/field';
import { MOBILE_SHOOT_ZONE_RATIO } from '../constants/hud';
import { getUiScale } from '../constants/scale';
import { sceneGrid } from '../utils/grid';
import { getLevelOverride, MAX_LEVEL } from '../constants/progression/LevelConfig';
import { mapProgress } from '../systems/MapProgress';
import { getSaveProvider } from '../systems/save/saveProviderFactory';
import { bindHaptics } from '../platform/hapticBindings';
import { bindAnalytics } from '../analytics/analyticsBindings';

// Аналитические события: отдельная шина 'analytics:*' от 'fx:*' (haptic) и
// доменных ('player:died', 'boss:*'). Haptic их НЕ слушает — только future
// PostHog-подписка. Payload'ы типизированы явно чтобы аналитический subscriber
// компилировался в parity с эмитами.
interface AnalyticsLevelStartPayload {
  level: number;
}
interface AnalyticsLevelCompletePayload {
  level: number;
  durationMs: number;
}
interface AnalyticsLevelFailedPayload {
  level: number;
  killedBy: string;
  wave: number;
}
import { generateWave, getWavesForLevel, MOUSE_GROUP_SIZE as WG_MOUSE_GROUP_SIZE } from '../systems/WaveGenerator';
import { VirtualJoystick } from '../entities/VirtualJoystick';
import type { Weapon } from '../entities/weapons/Weapon';
import { createRevolver } from '../entities/weapons/Revolver';
import { createDualRevolver } from '../entities/weapons/DualRevolver';
import { createUzi } from '../entities/weapons/Uzi';
import { createShotgun } from '../entities/weapons/Shotgun';
import { createAssaultRifle } from '../entities/weapons/AssaultRifle';
import { createSniper } from '../entities/weapons/Sniper';
import { createMachineGun } from '../entities/weapons/MachineGun';
import { createGrenadeLauncher } from '../entities/weapons/GrenadeLauncher';
import { createLaser } from '../entities/weapons/Laser';
import { MusicManager } from '../managers/MusicManager';
import { unlockAudioContext } from '../utils/audio';
import { isMobile } from '../utils/platform';
import { renderAllPuddles } from '../systems/puddles';

const PLAYER_X = 1080;
// Спавн привязан к ЛЕВОМУ КРАЮ CANVAS (не к границе поля). Враг создаётся
// за экраном (x = -50), идёт через зону леса (скрытую за спрайтом bg_forest
// на depth=10), эмерджит из леса в поле, проходит поле, скрывается за
// кукурузой (bg_corn на depth=10), и на x = scale.width + ENEMY_OFFSCREEN_MARGIN
// считается прорвавшимся. Полный path от левого края экрана до правого,
// независимо от ширины устройства.
const ENEMY_SPAWN_X_OFFSET = -50;
// Margin за правым краем сцены, после которого враг считается прорвавшимся.
// Использовано в проверке `enemy.x > this.scale.width + ENEMY_OFFSCREEN_MARGIN`.
const ENEMY_OFFSCREEN_MARGIN = 50;
// Разброс ±55px в группе мышей (поведенческий, не координата).
const MOUSE_GROUP_SPREAD = 55;
// Минимальное расстояние между Y соседних спавнов (для getNonOverlappingY).
const SPAWN_Y_GAP = 80;
const WAVE_BREAK_MS = 5000;
const STARTING_SPAWN_INTERVAL = 2000;
const MIN_SPAWN_INTERVAL = 600;
const WAVE_TEXT_Y = 60;
const WAVE_TEXT_FADE_DELAY_MS = 2000;
const WAVE_TEXT_FADE_DURATION_MS = 500;
const COUNTDOWN_TICK_MS = 1000;
const WAVE_BAR_WIDTH = 200;
const WAVE_BAR_HEIGHT = 6;
const WAVE_BAR_Y_OFFSET = 30;
const WAVE_BAR_RATIO_YELLOW = 0.5;
const WAVE_BAR_RATIO_RED = 0.2;
const HEADSHOT_SCORE = 25;
const SCORE_PADDING = 24;
const AMMO_FONT_SIZE = '20px';
const AMMO_Y_OFFSET = 30;
const AMMO_THRESHOLD_HIGH = 0.5;
const AMMO_THRESHOLD_LOW = 0.2;
// Reload-кнопка справа от лейбла «Защита: 100%» в верхнем ряду HUD'а.
// История: 60/165 (под HP-иконками — закрывалось iPhone notch'ем) → 220/50
// (впритык, налегала на HP-иконки) → 290/50 (в браузере ok, но в TG Mini App
// поверх кнопки «Закрыть» в шапке) → 400/50 (текущее — с запасом от TG-шапки
// и любых оверлеев мессенджера). TOP_OFFSET=50 — центр совпадает с defenseText.
const RELOAD_BTN_LEFT_OFFSET = 400;
const RELOAD_BTN_TOP_OFFSET = 50;
const RELOAD_BTN_RADIUS = 30;
// Было 0.6/0.3 — на светлой полянe тёмно-серая кнопка с alpha 0.6 терялась.
// 0.9/0.55 + жёлтая заливка + чёрный контур делают её заметной на любом фоне.
const RELOAD_BTN_ALPHA_IDLE = 0.9;
const RELOAD_BTN_ALPHA_DISABLED = 0.55;
// Цвет кнопки перезарядки. Жёлтый = «action/триумф» из палитры (тот же, что у
// HUD-цифр магазина-апгрейда), хорошо контрастирует с зелёной полянкой и
// тёмно-серым HUD. Чёрная иконка ⟲ + чёрный контур = максимальная читаемость.
const RELOAD_BTN_FILL = COLORS.yellow;
const RELOAD_BTN_STROKE = COLORS.black;
// HP-иконки игрока в левом верхнем углу — позиция фикс. от (0,0), на адаптиве не нужно перевычислять.
const PLAYER_HP_ICON_X = 50;
const PLAYER_HP_ICON_Y = 80;
const PLAYER_HP_ICON_GAP = 35;
const PLAYER_HP_ICON_SIZE = 25;
const WEAPON_SLOT_SIZE = 64;
const WEAPON_SLOT_GAP = 12;
// 50 (а не 20) — даёт ~30px запаса под home indicator iPhone в Safari (viewport-fit=cover).
// Слоты на Y=638 в сцене, видимы даже с home indicator.
const WEAPON_SLOTS_BOTTOM_PADDING = 50;
const WEAPON_SLOT_BORDER_WIDTH = 3;
const SLOT_COLOR_LOCKED = 0x2a2a2a;
const SLOT_COLOR_AVAILABLE = 0x4a4a4a;
const SLOT_COLOR_OWNED = 0x6a6a6a;
// Прогрессионно заблокированное (босс ещё не побеждён) — темнее обычного locked, без интеракции.
const SLOT_COLOR_PROGRESSION_LOCKED = 0x141414;
const SLOT_BORDER_DEFAULT = 0x000000;
const SLOT_BORDER_AVAILABLE = 0xffb800;
const SLOT_BORDER_ACTIVE = 0xe8e8e8;
const STARTING_DEFENSE = 100;
const DEFENSE_LOSS_PER_BREACH = 15;
const GAME_OVER_DELAY_MS = 1000;
const DEFENSE_THRESHOLD_HEALTHY = 60;
const DEFENSE_THRESHOLD_WARNING = 30;
const MAX_DELTA_MS = 100;
const EXPLOSION_DURATION_MS = 200;
const EXPLOSION_SHAKE_INTENSITY = 0.008;
const EXPLOSION_SHAKE_DURATION = 150;
const SHAKE_KILL_DURATION = 80;
const SHAKE_KILL_INTENSITY = 0.0005;
const SHAKE_HEADSHOT_DURATION = 120;
const SHAKE_HEADSHOT_INTENSITY = 0.0035;
const VOLUME_HIT = 0.5;
const VOLUME_DEATH = 0.5;
const VOLUME_BREACH = 0.7;
const VOLUME_WAVE_START = 0.5;
const VOLUME_WAVE_END = 0.4;
const VOLUME_GAME_OVER = 0.7;
const VOLUME_DEFENSE_LOW = 0.6;
const VOLUME_DEFENSE_CRITICAL = 0.7;
const ENEMY_DEATH_SOUND_DELAY_MS = 60;

// Чёрная обводка + мягкая тень — добавляем ко всем HUD-текстам, чтобы зелёный/
// оранжевый/белый текст не сливался со светлым полем боя. Включает stroke 4px
// (хватает для контраста) + тень с blur 2 (мягкость, ощущение глубины).
// Spread'ится в стиль создания каждого text-объекта.
const HUD_TEXT_OUTLINE = {
  stroke: '#000000',
  strokeThickness: 4,
  shadow: {
    offsetX: 0,
    offsetY: 2,
    color: '#000000',
    blur: 2,
    stroke: true,
    fill: true,
  },
} as const;
// Полупрозрачная плашка под кластером HUD-текстов — отдельный rounded-rect под
// каждой группой (защита, wave-баннер, деньги+патроны). PLATE_ALPHA 0.35 даёт
// дымку: текст контрастен, плашка не воспринимается тяжёлой подложкой.
const HUD_PLATE_ALPHA = 0.35;
const HUD_PLATE_PADDING = 8;
const HUD_PLATE_RADIUS = 6;

interface WeaponSlotUI {
  bg: Phaser.GameObjects.Rectangle;
  keyText: Phaser.GameObjects.Text;
  priceText?: Phaser.GameObjects.Text;
  icon?: Phaser.GameObjects.Image;
  slot: number;
  createFn: () => Weapon;
  price: number;
}

export class GameScene extends Phaser.Scene {
  private player!: Player;
  private bullets: Bullet[] = [];
  private grenades: Grenade[] = [];
  private beam?: Beam;
  private bossfightActive = false;
  private currentBoss: Boss | null = null;
  private enemies: Enemy[] = [];
  private scoreText!: Phaser.GameObjects.Text;
  private ammoText!: Phaser.GameObjects.Text;
  private score = 0;
  private spaceKey!: Phaser.Input.Keyboard.Key;
  private reloadKey!: Phaser.Input.Keyboard.Key;
  private joystick?: VirtualJoystick;
  private shootingPointerActive = false;
  private shootingPointerId: number | null = null;
  private reloadButton?: Phaser.GameObjects.Arc;
  private reloadIcon?: Phaser.GameObjects.Text;
  private weaponSlotsUI: WeaponSlotUI[] = [];
  // Фон поляны растягивается на весь canvas. Создаётся один раз в
  // createBackgrounds, размер/позиция пересчитываются на resize в
  // layoutBackgrounds. Боковые полосы леса/кукурузы убраны.
  private bgClearing?: Phaser.GameObjects.Image;
  // Процедурный muzzle-flash. Текстуры пекутся один раз в конструкторе FX.
  // depth выставляется ниже HUD (50) и выше игрока/врагов (0) — см. create().
  private muzzleFlash!: MuzzleFlashFX;
  private spawnTimer?: Phaser.Time.TimerEvent;
  private breakTimer?: Phaser.Time.TimerEvent;
  private enemiesInCurrentWave = 0;
  private enemiesRemainingInWave = 0;
  private enemiesSpawnedInWave = 0;
  private currentSpawnInterval = STARTING_SPAWN_INTERVAL;
  private currentWave = 1;
  private isInBreak = false;
  private playedDefenseLowSound = false;
  private playedDefenseCriticalSound = false;
  private waveText!: Phaser.GameObjects.Text;
  private waveBarBackground!: Phaser.GameObjects.Rectangle;
  private waveBarFill!: Phaser.GameObjects.Rectangle;
  // Один Graphics-объект на все 3 HUD-плашки (top-left защита, top-center
  // wave-баннер, top-right деньги+патроны). Перерисовывается в relayoutHUD.
  private hudPlates!: Phaser.GameObjects.Graphics;
  // Масштабированная ширина wave-bar (× uiScale на момент создания) — используется
  // в update'ах waveBarFill.width, иначе reset к WAVE_BAR_WIDTH=200 ломал согласованность.
  private waveBarMaxWidth = 0;
  private defense = STARTING_DEFENSE;
  private defenseText!: Phaser.GameObjects.Text;
  private playerHpIcons: Phaser.GameObjects.Rectangle[] = [];
  private instakillInProgress = false;
  private bossHpBarBg?: Phaser.GameObjects.Rectangle;
  private bossHpBarFill?: Phaser.GameObjects.Rectangle;
  private bossHpBarBorder?: Phaser.GameObjects.Rectangle;
  private bossNameText?: Phaser.GameObjects.Text;
  private bossPhaseText?: Phaser.GameObjects.Text;
  // Масштабированная ширина boss HP-бара (× uiScale на момент создания) — нужна
  // в updateBossHpBar для корректного fill.width = max * hpRatio.
  private bossHpBarMaxWidth = 0;
  private waveEventActive = false;
  private waveEventConfig: WaveEventConfig | null = null;
  private waveEventEnemiesTotal = 0;
  private waveEventEnemiesResolved = 0;
  private waveEventEnemiesSpawned = 0;
  private waveEventSpawnTimer?: Phaser.Time.TimerEvent;
  private waveProgressText?: Phaser.GameObjects.Text;
  private waveProgressBarBg?: Phaser.GameObjects.Rectangle;
  private waveProgressBarFill?: Phaser.GameObjects.Rectangle;
  private waveProgressBarMaxWidth = 0;
  private enemyProjectiles: EnemyProjectile[] = [];
  private zones: Zone[] = [];
  private readonly MAX_ZONES = 5;
  private currentLevel = 1;
  private wavesInCurrentLevel = 0;
  private currentWaveEntries: string[] = [];
  private currentWaveEntryIndex = 0;
  // ВНИМАНИЕ: this.currentWave теперь = wave-in-level counter (1..wavesInCurrentLevel).
  // Сбрасывается в 0 при startLevel.
  private startingLevel = 1;
  // Заработок за текущий бой. Коммитится в mapProgress.money ТОЛЬКО при первой победе.
  // При replay/проигрыше — сгорает (не переносится). Invariant: this.score - earnedThisLevel === mapProgress.money.
  private earnedThisLevel = 0;

  // Аналитика уровня: время старта (для durationMs) + флаг разрешения (защита от
  // двойного эмита complete+failed на один заход) + последний источник урона
  // (killedBy в failed-payload; сбрасывается на каждый startLevel).
  private levelStartedAt = 0;
  private levelResolved = false;
  private lastDamageSource: string | null = null;

  constructor() {
    super({ key: 'GameScene' });
  }

  init(data?: { level?: number }): void {
    this.startingLevel = data?.level ?? 1;
  }

  create(): void {
    const { width, height } = this.scale;
    const centerY = height / 2;

    this.cameras.main.setBackgroundColor(COLORS.darkGray);
    this.physics.world.setBounds(0, 0, width, height);
    this.input.mouse?.disableContextMenu();
    this.createBackgrounds();

    // Тактильная отдача через Telegram HapticFeedback (fx:*-эмиты + доменные
    // события). Подписка снимается на SHUTDOWN автоматически.
    bindHaptics(this);
    // PostHog-аналитика — независимая подписка на те же эмиты (analytics:*/
    // fx:*/boss:defeated). Cleanup на SHUTDOWN. В DEV / без ENV — no-op.
    bindAnalytics(this);

    // FX-сервисы: текстуры пекутся в конструкторе, готовы к первому fire().
    // depth 40 = выше игрока/врагов (0), ниже HUD (50) и boss bar (120).
    this.muzzleFlash = new MuzzleFlashFX(this);
    this.muzzleFlash.depth = 40;
    // Лужи-декали от смерти врагов накапливаются по матчам в пределах вкладки.
    // Создаются с depth = -5 (между clearing -20 и игровыми сущностями 0), остаются
    // под игроком и врагами. Depth-порядок жёстко зашит в render.
    renderAllPuddles(this);

    unlockAudioContext(this);

    this.score = mapProgress.getData().money;
    this.earnedThisLevel = 0;
    this.defense = STARTING_DEFENSE;
    this.currentLevel = 1;
    this.wavesInCurrentLevel = 0;
    this.currentWaveEntries = [];
    this.currentWaveEntryIndex = 0;
    this.playedDefenseLowSound = false;
    this.playedDefenseCriticalSound = false;
    this.currentSpawnInterval = STARTING_SPAWN_INTERVAL;
    this.isInBreak = false;
    this.bullets = [];
    this.enemies = [];
    this.enemyProjectiles = [];
    this.zones = [];
    this.bossfightActive = false;
    this.currentBoss = null;
    this.instakillInProgress = false;
    this.waveEventActive = false;
    this.waveEventConfig = null;
    this.waveEventEnemiesTotal = 0;
    this.waveEventEnemiesResolved = 0;
    this.waveEventEnemiesSpawned = 0;

    this.player = new Player(this, PLAYER_X, centerY);
    this.player.prewarm();

    // @ts-expect-error camera.update помечен protected в типах Phaser, но фактически работает.
    this.cameras.main.update(0, 0);

    const warmupBullet = new Bullet(this, -100, -100, Math.PI);
    this.bullets.push(warmupBullet);
    warmupBullet.destroy();
    this.bullets.length = 0;

    const warmupEnemy = new Enemy(this, -200, -200, getEnemyConfig('mouse'));
    this.enemies.push(warmupEnemy);
    warmupEnemy.destroy();
    this.enemies.length = 0;

    // HUD-тексты: размер шрифта × uiScale, с минимумом 11px (читаемость).
    // Позиции — все через relayoutHUD() (создаём в (0,0), потом ставим на места).
    const hudUi = getUiScale(this);
    const minFont = (basePx: number): string => `${Math.max(11, Math.round(basePx * hudUi))}px`;
    const waveFontSize = minFont(32);
    const scoreFontSize = minFont(24);
    const ammoFontSize = minFont(parseInt(AMMO_FONT_SIZE, 10));
    const waveBarWidth = WAVE_BAR_WIDTH * hudUi;
    const waveBarHeight = WAVE_BAR_HEIGHT * hudUi;
    this.waveBarMaxWidth = waveBarWidth;

    // HUD-depth 50 — поверх леса (depth 10), под Boss HP bar (120) и overlay (250+).
    // Иначе HUD-тексты в зонах леса (defenseText/scoreText/hp/reload) перекрываются.
    const HUD_DEPTH = 50;

    // Плашки под кластерами HUD-текстов. Полупрозрачный тёмный rounded-rect
    // под каждым кластером (Защита, Wave-баннер, Деньги+патроны). depth=49 —
    // выше леса, под текстами (50). Перерисовываются в relayoutHUD().
    this.hudPlates = this.add.graphics().setDepth(HUD_DEPTH - 1).setScrollFactor(0);

    this.waveText = this.add
      .text(0, 0, '', {
        fontFamily: 'Arial',
        fontSize: waveFontSize,
        color: HEX.white,
        fontStyle: 'bold',
        ...HUD_TEXT_OUTLINE,
      })
      .setOrigin(0.5, 0.5)
      .setDepth(HUD_DEPTH);

    this.waveBarBackground = this.add.rectangle(0, 0, waveBarWidth, waveBarHeight, COLORS.darkGray);
    this.waveBarBackground.setStrokeStyle(1, COLORS.midGray);
    this.waveBarBackground.setDepth(HUD_DEPTH);
    this.waveBarFill = this.add
      .rectangle(0, 0, waveBarWidth, waveBarHeight, COLORS.green)
      .setOrigin(1, 0.5)
      .setDepth(HUD_DEPTH);

    this.startLevel(this.startingLevel);

    this.scoreText = this.add
      .text(0, 0, this.formatScore(), {
        fontFamily: 'Arial',
        fontSize: scoreFontSize,
        color: HEX.white,
        fontStyle: 'bold',
        ...HUD_TEXT_OUTLINE,
      })
      .setOrigin(1, 0)
      .setDepth(HUD_DEPTH);

    this.ammoText = this.add
      .text(0, 0, '', {
        fontFamily: 'Arial',
        fontSize: ammoFontSize,
        color: HEX.green,
        fontStyle: 'bold',
        ...HUD_TEXT_OUTLINE,
      })
      .setOrigin(1, 0)
      .setDepth(HUD_DEPTH);

    this.createWeaponSlotsHUD();
    this.restoreOwnedWeapons();

    this.grenades = [];

    const laserCfg = createLaser().config;
    this.beam = new Beam(
      this,
      laserCfg.beamWidth ?? 8,
      laserCfg.rangePx ?? this.scale.width,
      COLORS.white,
      COLORS.green,
    );

    this.events.on('enemy:spawn-on-death', (data: { id: string; x: number; y: number }) => {
      const cfg = ENEMY_CONFIGS[data.id];
      if (!cfg) return;
      const child = new Enemy(this, data.x, data.y, cfg, true);
      this.enemies.push(child);
    });

    this.events.on('boss:defeated', () => this.onBossDefeated());
    this.events.on('player:died', () => this.handlePlayerDeath());
    this.events.on('boss:wall-impact', () => this.cameras.main.shake(200, 0.008));
    this.events.on('boss:phase-change', () => {
      this.currentBoss?.flashPhaseChange();
      this.cameras.main.shake(200, 0.012);
    });
    this.events.on(
      'boss:leave-slime',
      (data: {
        x: number;
        y: number;
        radius: number;
        color: number;
        slowFactor: number;
        textureKey?: string;
        displayDiameterPx?: number;
        lifetimeMs?: number;
      }) => {
        this.addZone(
          data.x,
          data.y,
          data.radius,
          data.color,
          data.slowFactor,
          'slow',
          data.textureKey,
          data.displayDiameterPx,
          data.lifetimeMs,
        );
      },
    );
    this.events.on(
      'boss:throw-projectile',
      (data: {
        startX: number;
        startY: number;
        targetX: number;
        targetY: number;
        speed: number;
        size: number;
        color: number;
        blockDurationMs: number;
        effectType?: 'block_shooting' | 'slow' | 'death';
        slowFactor?: number;
        textureKey?: string;
        animKey?: string;
        spriteHeightPx?: number;
        useArc?: boolean;
        gravity?: number;
        arcHeight?: number;
        rotationSpeed?: number;
        landSlimeConfig?: import('../entities/BossConfig').LandSlimeConfig;
      }) => {
        const proj = new EnemyProjectile(
          this,
          data.startX,
          data.startY,
          data.targetX,
          data.targetY,
          data.speed,
          data.size,
          data.color,
          data.blockDurationMs,
          data.effectType ?? 'block_shooting',
          data.slowFactor ?? 1.0,
          data.textureKey,
          data.animKey,
          data.spriteHeightPx,
          data.useArc,
          data.gravity ?? 1500,
          data.arcHeight ?? 200,
          data.rotationSpeed ?? 0,
          data.landSlimeConfig,
        );
        this.enemyProjectiles.push(proj);
      },
    );


    // defense — шрифт × uiScale, согласовано со score/ammo (min 11px). Позиция — в relayoutHUD.
    const defenseUi = getUiScale(this);
    const defenseFontSize = `${Math.max(11, Math.round(24 * defenseUi))}px`;
    this.defenseText = this.add
      .text(0, 0, this.formatDefense(), {
        fontFamily: 'Arial',
        fontSize: defenseFontSize,
        color: HEX.green,
        fontStyle: 'bold',
        ...HUD_TEXT_OUTLINE,
      })
      .setOrigin(0, 0)
      .setDepth(50);

    this.spaceKey = this.input.keyboard!.addKey(Phaser.Input.Keyboard.KeyCodes.SPACE);

    const key1 = this.input.keyboard!.addKey(Phaser.Input.Keyboard.KeyCodes.ONE);
    const key2 = this.input.keyboard!.addKey(Phaser.Input.Keyboard.KeyCodes.TWO);
    const key3 = this.input.keyboard!.addKey(Phaser.Input.Keyboard.KeyCodes.THREE);
    const key4 = this.input.keyboard!.addKey(Phaser.Input.Keyboard.KeyCodes.FOUR);
    const key5 = this.input.keyboard!.addKey(Phaser.Input.Keyboard.KeyCodes.FIVE);
    const key6 = this.input.keyboard!.addKey(Phaser.Input.Keyboard.KeyCodes.SIX);
    const key7 = this.input.keyboard!.addKey(Phaser.Input.Keyboard.KeyCodes.SEVEN);
    const key8 = this.input.keyboard!.addKey(Phaser.Input.Keyboard.KeyCodes.EIGHT);
    const key9 = this.input.keyboard!.addKey(Phaser.Input.Keyboard.KeyCodes.NINE);
    this.reloadKey = this.input.keyboard!.addKey(Phaser.Input.Keyboard.KeyCodes.R);
    this.reloadKey.on('down', () => {
      this.player.currentWeapon.startReload(this);
    });
    key1.on('down', () => {
      this.player.inventory.switchToSlot(0);
    });
    key2.on('down', () => {
      this.tryBuyOrSwitchWeapon(1, createDualRevolver);
    });
    key3.on('down', () => {
      this.tryBuyOrSwitchWeapon(2, createAssaultRifle);
    });
    key4.on('down', () => {
      this.tryBuyOrSwitchWeapon(3, createShotgun);
    });
    key5.on('down', () => {
      this.tryBuyOrSwitchWeapon(4, createUzi);
    });
    key6.on('down', () => {
      this.tryBuyOrSwitchWeapon(5, createSniper);
    });
    key7.on('down', () => {
      this.tryBuyOrSwitchWeapon(6, createMachineGun);
    });
    key8.on('down', () => {
      this.tryBuyOrSwitchWeapon(7, createGrenadeLauncher);
    });
    key9.on('down', () => {
      this.tryBuyOrSwitchWeapon(8, createLaser);
    });

    if (isMobile()) {
      this.joystick = new VirtualJoystick(this);
      // Явный destroy при shutdown сцены — иначе при переходе на MapScene
      // base/knob ringbuf'ы джойстика и его pointerdown-подписки на input plugin
      // остаются "сиротами", и капсула джойстика мелькает на карте.
      this.events.once(Phaser.Scenes.Events.SHUTDOWN, () => {
        this.joystick?.destroy();
        this.joystick = undefined;
      });
      // Бывшая нижняя deadzone убрана: пальцы внизу = типичная точка
      // стрельбы/движения, deadzone перекрывала врагов. Слоты оружия защищены
      // своим object-level pointerdown + event.stopPropagation() — тап по
      // слоту не «утечёт» в стрельбу/джойстик через scene-level input.on.
      this.input.on('pointerdown', (pointer: Phaser.Input.Pointer) => {
        if (pointer.x < this.scale.width * MOBILE_SHOOT_ZONE_RATIO) {
          this.shootingPointerActive = true;
          this.shootingPointerId = pointer.id;
          if (this.player.currentWeapon.getFireMode() === 'single') {
            this.shoot();
          }
        }
      });
      const releasePointer = (pointer: Phaser.Input.Pointer): void => {
        if (pointer.id === this.shootingPointerId) {
          this.shootingPointerActive = false;
          this.shootingPointerId = null;
        }
      };
      this.input.on('pointerup', releasePointer);
      this.input.on('pointerupoutside', releasePointer);

      // Reload-кнопка масштабируется uiScale — на 360 в два раза меньше, не съедает угол.
      // Согласовано с deadzone в VirtualJoystick (тоже × uiScale, см. там).
      // Позиция — в relayoutHUD; здесь только размер и шрифт.
      const reloadUiScale = getUiScale(this);
      const reloadBtnRadius = RELOAD_BTN_RADIUS * reloadUiScale;
      const reloadIconFontSize = Math.max(14, Math.round(32 * reloadUiScale));

      this.reloadButton = this.add
        .circle(0, 0, reloadBtnRadius, RELOAD_BTN_FILL, RELOAD_BTN_ALPHA_IDLE)
        .setStrokeStyle(3, RELOAD_BTN_STROKE, 1)
        .setDepth(100)
        .setScrollFactor(0)
        .setInteractive();

      this.reloadIcon = this.add
        .text(0, 0, '⟲', {
          fontFamily: 'Arial',
          fontSize: `${reloadIconFontSize}px`,
          color: HEX.black,
          fontStyle: 'bold',
        })
        .setOrigin(0.5)
        .setDepth(101)
        .setScrollFactor(0);

      this.reloadButton.on(
        'pointerdown',
        (
          _pointer: Phaser.Input.Pointer,
          _x: number,
          _y: number,
          event: Phaser.Types.Input.EventData,
        ) => {
          this.player.currentWeapon.startReload(this);
          event.stopPropagation();
          this.tweens.add({
            targets: [this.reloadButton, this.reloadIcon],
            scale: 0.9,
            duration: 80,
            yoyo: true,
          });
        },
      );
    }

    const tickerObj = this.add.rectangle(-1000, -1000, 1, 1, 0x000000, 0);
    this.tweens.add({
      targets: tickerObj,
      x: -1001,
      duration: 16,
      yoyo: true,
      repeat: -1,
    });

    MusicManager.getInstance().playCombat(this);

    // HUD-разметка через grid 12×9 (см. relayoutHUD). Один вызов после создания
    // всех HUD-объектов синхронизирует их с текущим scale; подписка на resize
    // делает HUD реактивным к смене размера окна. Отписка в SHUTDOWN — иначе
    // обработчик копится между рестартами сцены.
    this.relayoutHUD();
    this.scale.on('resize', this.relayoutHUD, this);
    this.events.once(Phaser.Scenes.Events.SHUTDOWN, () => {
      this.scale.off('resize', this.relayoutHUD, this);
    });
  }

  override update(_time: number, delta: number): void {
    const cappedDelta = Math.min(delta, MAX_DELTA_MS);

    if (this.joystick) {
      this.player.joystickInput = this.joystick.getDirection();
    }
    this.player.update(cappedDelta);

    const inputHeld = this.spaceKey.isDown || this.shootingPointerActive;
    const weapon = this.player.currentWeapon;
    weapon.tickWindup(cappedDelta, inputHeld);

    if (this.beam && this.beam.isActive() && !weapon.isBeam()) {
      this.beam.deactivate();
    }

    const fireMode = weapon.getFireMode();
    if (fireMode === 'beam') {
      this.handleBeamWeapon(weapon, cappedDelta, inputHeld);
    } else if (fireMode === 'auto') {
      if (this.spaceKey.isDown) this.shoot();
      if (this.shootingPointerActive) this.shoot();
    } else {
      if (Phaser.Input.Keyboard.JustDown(this.spaceKey)) this.shoot();
    }

    const { width, height } = this.scale;

    for (const enemy of this.enemies) {
      const target =
        enemy instanceof Boss
          ? { x: this.player.x, y: this.player.y }
          : { x: width + 100, y: enemy.y };
      enemy.tick(cappedDelta, target);
    }

    if (this.bossfightActive && this.currentBoss && this.currentBoss.currentHp <= 0) {
      if (this.currentBoss.shouldReviveInsteadOfDeath()) {
        this.handleBossFakeDeath(this.currentBoss);
      } else if (!this.currentBoss.isInFakeDeath) {
        this.events.emit('boss:defeated');
      }
    }

    if (this.bossfightActive && this.currentBoss && !this.currentBoss.isInFakeDeath) {
      const boss = this.currentBoss;
      const dx = Math.abs(boss.x - this.player.x);
      const dy = Math.abs(boss.y - this.player.y);
      // displayWidth/Height учитывают setScale (после revive size ×0.85).
      const collisionDistanceX = (boss.displayWidth + this.player.hitboxWidth) / 2;
      const collisionDistanceY = (boss.displayHeight + this.player.hitboxHeight) / 2;
      if (dx < collisionDistanceX && dy < collisionDistanceY) {
        if (boss.bossConfig.contactBehavior === 'instakill') {
          this.handleInstakillContact(boss);
        } else {
          // Detect real (non-blocked) hit through boarHits diff — invulnerable passes
          // return false too, this lets shake fire only on actual damage.
          const hitsBefore = this.player.boarHits;
          const wasFatal = this.player.applyBoarHit();
          const tookHit = this.player.boarHits < hitsBefore;
          if (tookHit) {
            // Источник урона — этот босс. Пишем даже на нефатальный удар, потому
            // что финальный (fatal) сюда попадёт с той же ссылкой на boss.
            this.lastDamageSource = boss.config.id;
          }
          if (tookHit && !wasFatal) {
            this.updatePlayerHpDisplay();
            this.cameras.main.shake(150, 0.012);
            this.events.emit('fx:player-hurt');
          }
          // Fatal: handled via 'player:died' event → handlePlayerDeath
        }
      }
      this.updateBossHpBar();
      this.updateBossPhaseText();
      this.updateZoneEffects();
    }

    for (let eIdx = this.enemies.length - 1; eIdx >= 0; eIdx--) {
      const enemy = this.enemies[eIdx];
      const enemyHalfW = enemy.width / 2;
      const enemyHalfH = enemy.height / 2;
      let enemyDead = false;

      for (let bIdx = this.bullets.length - 1; bIdx >= 0; bIdx--) {
        const bullet = this.bullets[bIdx];
        const dx = Math.abs(bullet.x - enemy.x);
        const dy = Math.abs(bullet.y - enemy.y);
        const bulletHalfW = bullet.width / 2;
        const bulletHalfH = bullet.height / 2;

        if (dx >= enemyHalfW + bulletHalfW || dy >= enemyHalfH + bulletHalfH) {
          continue;
        }

        bullet.destroy();
        this.bullets.splice(bIdx, 1);

        if (!enemy.getIsDying()) {
          this.sound.play('hit_body', { volume: VOLUME_HIT });
          this.events.emit('fx:hit');
          const isHeadshot = enemy.getHitZone(bullet.y) === 'head';
          if (isHeadshot) this.events.emit('fx:headshot');
          const killed = enemy.damage(bullet.damage);

          if (killed) {
            // Босс с reviveOnce — фейк-смерть, не реальная. Не сплайсим/не destroy'им,
            // не даём очков, не играем death-звук. Update-цикл подхватит hp<=0 и
            // вызовет handleBossFakeDeath. Очки начислятся при настоящей смерти.
            if (enemy instanceof Boss && enemy.shouldReviveInsteadOfDeath()) {
              break;
            }
            if (isHeadshot) {
              this.cameras.main.shake(SHAKE_HEADSHOT_DURATION, SHAKE_HEADSHOT_INTENSITY);
              this.awardScore(HEADSHOT_SCORE);
            } else {
              this.cameras.main.shake(SHAKE_KILL_DURATION, SHAKE_KILL_INTENSITY);
              this.awardScore(enemy.config.scoreReward);
            }
            this.scoreText.setText(this.formatScore());
            this.enemies.splice(eIdx, 1);
            this.events.emit('fx:kill');
            this.time.delayedCall(ENEMY_DEATH_SOUND_DELAY_MS, () => {
              this.sound.play('enemy_death', { volume: VOLUME_DEATH });
            });
            enemy.dieWithKnockback(() => {});
            enemyDead = true;
            this.onEnemyResolved(enemy);
            break;
          }
          // Не убит — bullet поглощена, продолжаем по другим пулям этого врага
        }
      }

      if (!enemyDead && !(enemy instanceof Boss) && enemy.x > this.scale.width + ENEMY_OFFSCREEN_MARGIN) {
        enemy.destroy();
        this.enemies.splice(eIdx, 1);

        if (!this.isInBreak) {
          this.sound.play('enemy_breach', { volume: VOLUME_BREACH });
          this.events.emit('fx:breach');
          this.defense = Math.max(0, this.defense - DEFENSE_LOSS_PER_BREACH);
          this.updateDefenseDisplay();
          this.onEnemyResolved(enemy);

          if (this.defense === 0) {
            // Смерть от накопленных прорывов — не отдельный враг, а истощение.
            // Перебивает возможные предыдущие boss-hit источники (последний
            // фатальный источник — истощение защиты).
            this.lastDamageSource = 'defense_depleted';
            this.gameOver();
            return;
          }
        }
      }
    }

    for (let i = this.bullets.length - 1; i >= 0; i--) {
      const bullet = this.bullets[i];
      if (bullet.x < 0 || bullet.x > width || bullet.y < 0 || bullet.y > height) {
        bullet.destroy();
        this.bullets.splice(i, 1);
      }
    }

    for (let i = this.grenades.length - 1; i >= 0; i--) {
      const grenade = this.grenades[i];
      const reachedEnd = grenade.tick(cappedDelta);

      let hitEnemy = false;
      if (!reachedEnd) {
        for (const enemy of this.enemies) {
          const dx = Math.abs(enemy.x - grenade.x);
          const dy = Math.abs(enemy.y - grenade.y);
          if (
            dx < enemy.width / 2 + GRENADE_RADIUS &&
            dy < enemy.height / 2 + GRENADE_RADIUS
          ) {
            hitEnemy = true;
            break;
          }
        }
      }

      if (reachedEnd || hitEnemy) {
        this.explodeGrenade(grenade.x, grenade.y, grenade.explosionRadius);
        grenade.destroy();
        this.grenades.splice(i, 1);
      }
    }

    for (let i = this.enemyProjectiles.length - 1; i >= 0; i--) {
      const proj = this.enemyProjectiles[i];
      proj.update(cappedDelta);

      const dx = Math.abs(proj.x - this.player.x);
      const dy = Math.abs(proj.y - this.player.y);
      const collisionX = this.player.hitboxWidth / 2 + proj.radius;
      const collisionY = this.player.hitboxHeight / 2 + proj.radius;
      if (dx < collisionX && dy < collisionY) {
        if (proj.effectType === 'death') {
          this.cameras.main.shake(200, 0.015);
          proj.destroy();
          this.enemyProjectiles.splice(i, 1);
          this.player.die();
          return;
        }
        if (proj.effectType === 'slow') {
          this.player.applyTemporarySlow(proj.slowFactor, proj.effectDurationMs);
        } else {
          this.player.blockShootingFor(proj.effectDurationMs);
        }
        this.cameras.main.shake(150, 0.008);
        proj.destroy();
        this.enemyProjectiles.splice(i, 1);
        continue;
      }

      // Приземление по дуге (без попадания) — спавним landSlimeConfig зону,
      // если она задана. Универсальный механизм: rocket-style боссы оставляют
      // лужу/хазард в точке промаха. Текущий медведь (после PROJECT.md revert)
      // landSlimeConfig не использует — слизь у него от конца charge.
      if (proj.hasLanded) {
        const sc = proj.landSlimeConfig;
        if (sc) {
          this.addZone(
            proj.x,
            proj.y,
            sc.radius,
            0x5a7a3a,
            sc.slowFactor ?? 1.0,
            sc.effectType,
            sc.textureKey,
            sc.displayDiameterPx,
            sc.lifetimeMs,
          );
        }
        proj.destroy();
        this.enemyProjectiles.splice(i, 1);
        continue;
      }

      if (
        proj.x < -50 ||
        proj.x > this.scale.width + 50 ||
        proj.y < -50 ||
        proj.y > this.scale.height + 50
      ) {
        proj.destroy();
        this.enemyProjectiles.splice(i, 1);
      }
    }

    this.updateAmmoDisplay();
    this.updateWeaponSlotsHUD();

    if (this.reloadButton) {
      const isReloading = this.player.currentWeapon.getIsReloading();
      const targetAlpha = isReloading ? RELOAD_BTN_ALPHA_DISABLED : RELOAD_BTN_ALPHA_IDLE;
      this.reloadButton.setFillStyle(RELOAD_BTN_FILL, targetAlpha);
    }
  }

  private shoot(): void {
    if (this.player.isShootingBlocked) return;
    const weapon = this.player.currentWeapon;
    if (weapon.isBeam()) return;
    const now = this.time.now;
    if (!weapon.canFire(now)) return;

    const { x, y } = weapon.config.isDual
      ? this.player.getMuzzlePositionForBarrel(weapon.getNextBarrel())
      : this.player.getMuzzlePosition();

    if (weapon.config.isGrenade) {
      const range = weapon.config.rangePx ?? this.scale.width / 3;
      this.grenades.push(
        new Grenade(
          this,
          x,
          y,
          -range,
          weapon.config.flightDurationMs ?? 1200,
          weapon.config.arcHeight ?? 120,
          weapon.config.explosionRadius ?? 100,
        ),
      );
    } else {
      const angles = weapon.computeBulletAngles(Math.PI);
      for (const angle of angles) {
        this.bullets.push(
          new Bullet(
            this,
            x,
            y,
            angle,
            weapon.config.bulletWidth,
            weapon.config.bulletHeight,
            weapon.config.bulletSpeed,
            weapon.config.damage,
          ),
        );
      }
    }
    weapon.recordFire(now);

    if (weapon.config.recoilForce) {
      this.player.applyRecoil(weapon.config.recoilForce);
    }

    // Визуальная отдача — кик спрайта против facing'а через twin'ы (≠ applyRecoil,
    // который сдвигает позицию тела для геймплейного эффекта). Срабатывает
    // КАЖДЫЙ выстрел, включая full-auto — playRecoil сам kill'ит активный кик.
    this.player.playRecoil();

    // Muzzle flash: для dual — обе бочки одновременно (один bullet, две вспышки —
    // стилистический акцент дуального ствола). Math.PI = угол прицела (влево,
    // соответствует facing-left деда). isBeam уже отсечён в начале shoot().
    if (weapon.config.isDual) {
      const m0 = this.player.getMuzzlePositionForBarrel(0);
      const m1 = this.player.getMuzzlePositionForBarrel(1);
      this.muzzleFlash.fire(m0.x, m0.y, Math.PI, weapon.config.id);
      this.muzzleFlash.fire(m1.x, m1.y, Math.PI, weapon.config.id);
    } else {
      this.muzzleFlash.fire(x, y, Math.PI, weapon.config.id);
    }
    this.sound.play(weapon.config.soundKey, { volume: weapon.config.soundVolume });
    if (weapon.config.shakeShoot) {
      const { intensity, duration } = weapon.config.shakeShoot;
      this.cameras.main.shake(duration, intensity);
    }

    if (weapon.needsReload()) {
      weapon.startReload(this);
    }
  }

  private handleBeamWeapon(weapon: Weapon, deltaMs: number, inputHeld: boolean): void {
    if (!this.beam) return;
    if (this.player.isShootingBlocked) {
      if (this.beam.isActive()) this.beam.deactivate();
      return;
    }
    const wasActive = this.beam.isActive();
    const wantsFire = inputHeld && weapon.canFireBeam(wasActive);

    if (!wantsFire) {
      if (wasActive) this.beam.deactivate();
      weapon.regenEnergy(deltaMs);
      return;
    }

    weapon.drainEnergy(deltaMs);
    if (!wasActive) {
      this.beam.activate();
      this.cameras.main.shake(60, 0.002);
      // Один кик на активацию луча — beam-то непрерывный, не имеет смысла
      // долбить recoil каждый кадр (получится дрожь спрайта).
      this.player.playRecoil();
    }
    const muzzle = this.player.getMuzzlePosition();
    this.beam.setOrigin(muzzle.x, muzzle.y);
    this.beam.redraw();

    const dps = weapon.config.damagePerSecond ?? 0;
    const damagePerFrame = (dps * deltaMs) / 1000;
    const hits = this.beam.getHitEnemies(this.enemies);
    for (const enemy of hits) {
      const killed = enemy.damage(damagePerFrame);
      if (!killed) continue;
      // Босс с reviveOnce — фейк-смерть, обработается через update intercept.
      if (enemy instanceof Boss && enemy.shouldReviveInsteadOfDeath()) continue;
      const idx = this.enemies.indexOf(enemy);
      if (idx < 0) continue;
      this.awardScore(enemy.config.scoreReward);
      this.scoreText.setText(this.formatScore());
      this.enemies.splice(idx, 1);
      enemy.dieWithKnockback(() => {});
      this.onEnemyResolved(enemy);
    }
  }

  private explodeGrenade(centerX: number, centerY: number, radius: number): void {
    const visual = this.add.circle(centerX, centerY, radius, COLORS.yellow, 0.6);
    visual.setDepth(90);
    this.tweens.add({
      targets: visual,
      alpha: 0,
      scale: 1.2,
      duration: EXPLOSION_DURATION_MS,
      onComplete: () => visual.destroy(),
    });
    this.cameras.main.shake(EXPLOSION_SHAKE_DURATION, EXPLOSION_SHAKE_INTENSITY);

    const cfg = this.player.currentWeapon.config;
    const dmgCenter = cfg.damageCenter ?? 8;
    const dmgEdge = cfg.damageEdge ?? 2;

    for (let i = this.enemies.length - 1; i >= 0; i--) {
      const enemy = this.enemies[i];
      const dx = enemy.x - centerX;
      const dy = enemy.y - centerY;
      const dist = Math.sqrt(dx * dx + dy * dy);
      if (dist > radius) continue;
      const dmg = dmgCenter - (dmgCenter - dmgEdge) * (dist / radius);
      const killed = enemy.damage(dmg);
      if (killed) {
        // Босс с reviveOnce — фейк-смерть, обработается через update intercept.
        if (enemy instanceof Boss && enemy.shouldReviveInsteadOfDeath()) continue;
        this.awardScore(enemy.config.scoreReward);
        this.scoreText.setText(this.formatScore());
        this.enemies.splice(i, 1);
        enemy.dieWithKnockback(() => {});
        this.onEnemyResolved(enemy);
      }
    }
  }

  private spawnWave(): void {
    if (this.bossfightActive) return;
    if (this.waveEventActive) return;
    if (this.currentWaveEntryIndex >= this.currentWaveEntries.length) {
      this.spawnTimer?.destroy();
      return;
    }

    const y = this.getNonOverlappingY([]);
    if (y === null) return;

    const id = this.currentWaveEntries[this.currentWaveEntryIndex];
    this.currentWaveEntryIndex += 1;

    // Спавн от ЛЕВОГО КРАЯ CANVAS (не от границы поля) — враг идёт через
    // всю зону леса до игрока.
    const spawnX = ENEMY_SPAWN_X_OFFSET;
    if (id === 'mouse') {
      this.spawnEnemyGroup('mouse', WG_MOUSE_GROUP_SIZE, spawnX, y, MOUSE_GROUP_SPREAD);
      this.enemiesSpawnedInWave += WG_MOUSE_GROUP_SIZE;
    } else {
      this.enemies.push(new Enemy(this, spawnX, y, getEnemyConfig(id)));
      this.enemiesSpawnedInWave += 1;
    }
  }

  private spawnEnemyGroup(
    typeId: string,
    count: number,
    centerX: number,
    centerY: number,
    spread: number,
  ): void {
    const config = getEnemyConfig(typeId);
    // Кламп центра группы: getNonOverlappingY клампит centerY в [top..reachable],
    // но разброс ±spread может вытолкнуть крайнюю мышь за достижимую зону (нижняя →
    // мёртвая зона муззла; верхняя → за поле). Сжимаем "коридор" центра на spread
    // с обеих сторон — тогда любая мышь centerY ± spread гарантированно достижима.
    // Альтернатива (кламп каждой мыши после разброса) сжимала бы группу в линию у границ;
    // здесь группа сохраняет полный spread, просто центр не подходит к краю ближе чем spread.
    const minCenterY = getFieldTopY(this) + spread;
    const maxCenterY = this.getReachableSpawnYMax() - spread;
    const safeCenterY = Phaser.Math.Clamp(centerY, minCenterY, maxCenterY);
    for (let i = 0; i < count; i++) {
      const ox = (Math.random() - 0.5) * 2 * spread;
      const oy = (Math.random() - 0.5) * 2 * spread;
      const enemy = new Enemy(this, centerX + ox, safeCenterY + oy, config);
      this.enemies.push(enemy);
    }
  }

  private triggerBossfight(bossId: string): void {
    if (this.bossfightActive) return;
    if (this.waveEventActive) return;

    // Чистим все обычные враги — волна прервана.
    for (const enemy of this.enemies) {
      if (!enemy.getIsDying()) enemy.destroy();
    }
    this.enemies = [];
    this.enemiesRemainingInWave = 0;
    this.enemiesSpawnedInWave = 0;

    // Останавливаем spawn-timer обычных волн.
    this.spawnTimer?.destroy();

    this.bossfightActive = true;

    // Скрываем wave-prep UI (label/плашка/прогресс-бар обычной волны), чтобы
    // не дублировал boss HP/имя. Восстанавливать вручную не нужно: boss-уровни
    // всегда уходят в returnToMapVictory → scene restart → свежее состояние.
    this.hideRegularWaveUI();

    const cfg = getBossConfig(bossId);
    // 200px от левой границы поля = X=300 на сцене 1280×720 (FIELD_LEFT_X=100 + 200).
    // На адаптивном размере сохранит ту же дистанцию "от стены" вместо плавающего абсолютного X.
    const bossX = getFieldLeftX(this) + 200;
    const bossY = (getFieldTopY(this) + getFieldBottomY(this)) / 2;
    const boss = new Boss(this, bossX, bossY, cfg);
    this.currentBoss = boss;
    this.enemies.push(boss);

    this.createPlayerHpDisplay();
    this.updatePlayerHpDisplay();
    this.createBossHpBar(boss);
    this.updateBossHpBar();
    this.showBossIntro(`БОСС: ${cfg.displayName.toUpperCase()}`);
  }

  private clearEnemyProjectiles(): void {
    for (const proj of this.enemyProjectiles) proj.destroy();
    this.enemyProjectiles = [];
  }

  // Унифицированный спавн зоны. Все опциональные параметры — для спрайт-зон
  // (медведь: bear_slime) и time-limited зон (камень-промах через
  // landSlimeConfig). Дефолт = 'slow', без спрайта, без lifetime.
  private addZone(
    x: number,
    y: number,
    radius: number,
    color: number,
    slowFactor: number,
    effectType: import('../entities/Zone').ZoneEffectType = 'slow',
    textureKey?: string,
    displayDiameterPx?: number,
    lifetimeMs?: number,
  ): void {
    const zone = new Zone(
      this,
      x,
      y,
      radius,
      color,
      slowFactor,
      effectType,
      textureKey,
      displayDiameterPx,
      lifetimeMs,
    );
    this.zones.push(zone);
    // FIFO: при превышении лимита удаляем самую старую (head).
    while (this.zones.length > this.MAX_ZONES) {
      const oldest = this.zones.shift();
      oldest?.destroy();
    }
  }

  private updateZoneEffects(): void {
    let activeSlowFactor = 1.0;
    for (const zone of this.zones) {
      if (!zone.contains(this.player.x, this.player.y)) continue;
      if (zone.effectType === 'death') {
        // Instakill: emit 'player:died' → handlePlayerDeath чистит zones.
        // Дальнейшее обновление множителя скорости не нужно — сцена сворачивается.
        this.player.die();
        return;
      }
      activeSlowFactor = zone.slowFactor;
      break;
    }
    this.player.speedMultiplier = activeSlowFactor;
  }

  private clearZones(): void {
    for (const zone of this.zones) zone.destroy();
    this.zones = [];
    this.player.speedMultiplier = 1.0;
  }

  private onBossDefeated(): void {
    // isFinalBoss читаем ДО зануления currentBoss.
    const isFinal = this.currentBoss?.bossConfig.isFinalBoss === true;

    this.bossfightActive = false;
    this.currentBoss = null;
    this.instakillInProgress = false;
    this.clearEnemyProjectiles();
    this.clearZones();
    this.player.restoreBoarHits();
    this.destroyPlayerHpDisplay();
    this.destroyBossHpBar();

    if (isFinal) {
      this.showFinalVictory();
    } else {
      this.showBossVictory('БОСС ПОВЕРЖЕН');
      // Босс-уровень — это весь уровень. Сразу переходим к следующему (без break).
      this.onLevelComplete();
    }
  }

  private showFinalVictory(): void {
    this.events.emit('fx:final-victory');
    // L50 не идёт через onLevelComplete (там ранний return на MAX_LEVEL),
    // поэтому analytics:level-complete здесь. Идемпотентен через levelResolved.
    this.emitAnalyticsLevelComplete();
    // Останавливаем всё что могло работать в фоне.
    this.spawnTimer?.destroy();
    this.breakTimer?.destroy();
    this.waveEventSpawnTimer?.destroy();
    this.waveBarBackground.setVisible(false);
    this.waveBarFill.setVisible(false);
    MusicManager.getInstance().stop(this);

    this.add
      .rectangle(
        this.scale.width / 2,
        this.scale.height / 2,
        this.scale.width,
        this.scale.height,
        0x000000,
        0.7,
      )
      .setScrollFactor(0)
      .setDepth(300);

    this.add
      .text(this.scale.width / 2, this.scale.height / 2 - 40, 'ПОБЕДА!', {
        fontFamily: 'Arial',
        fontSize: '96px',
        color: '#FFD700',
        fontStyle: 'bold',
        stroke: '#000000',
        strokeThickness: 6,
      })
      .setOrigin(0.5)
      .setScrollFactor(0)
      .setDepth(301);

    this.add
      .text(
        this.scale.width / 2,
        this.scale.height / 2 + 50,
        'Лес защищён. Все боссы повержены.',
        {
          fontFamily: 'Arial',
          fontSize: '28px',
          color: HEX.white,
        },
      )
      .setOrigin(0.5)
      .setScrollFactor(0)
      .setDepth(301);

    // Финальный босс (Медведь L50) — возврат на карту через тот же путь, что и обычная победа
    // (commit earnings → completeLevel → scene.start).
    this.time.delayedCall(5000, () => this.returnToMapVictory());
  }

  // Фоны: поляна на поле (центр, depth -20 — ниже луж -5) + боковые полосы
  // Поляна растягивается на весь canvas (depth -20, под всеми сущностями).
  // Боковые полосы леса/кукурузы убраны — враги видны с момента входа в canvas.
  // Image создаётся один раз; resize меняет только setDisplaySize/setPosition
  // через layoutBackgrounds. Если текстура не загрузилась — Image не создаётся,
  // фолбэк-цвет cameras.main.setBackgroundColor остаётся видимым.
  private createBackgrounds(): void {
    if (this.textures.exists('bg_clearing')) {
      this.bgClearing = this.add
        .image(0, 0, 'bg_clearing')
        .setOrigin(0.5)
        .setDepth(-20);
    }
    this.layoutBackgrounds();

    // Resize: подгонять размеры/позиции под новый scale. Phaser сам не пересчитает
    // Image при изменении окна. Отписка в SHUTDOWN — иначе scale.on копится между
    // рестартами сцены.
    this.scale.on('resize', this.layoutBackgrounds, this);
    this.events.once(Phaser.Scenes.Events.SHUTDOWN, () => {
      this.scale.off('resize', this.layoutBackgrounds, this);
    });
  }

  private layoutBackgrounds(): void {
    if (!this.bgClearing) return;
    const sceneW = this.scale.width;
    const sceneH = this.scale.height;
    // Поляна — на весь canvas (без сохранения пропорций). Origin (0.5, 0.5),
    // позиция в центре canvas, setDisplaySize под полные размеры сцены.
    this.bgClearing.setPosition(sceneW / 2, sceneH / 2);
    this.bgClearing.setDisplaySize(sceneW, sceneH);
  }

  // Перевыставляет позиции всех HUD-элементов через grid-сетку 12×9. Вызывается
  // после create() (когда HUD создан) и на каждый resize окна. Размеры (шрифт,
  // ширина бара, размер слота) остаются × uiScale — grid отвечает только за
  // якорные точки (углы и центр верхней/нижней строки). offset'ы внутри ячеек
  // (например padding 24 у defense/score) — аналог Bootstrap padding-utility,
  // не магия. Защищается guard'ами от частично созданного HUD (boss bar и
  // hp-icons появляются по факту).
  private relayoutHUD(): void {
    const ui = getUiScale(this);
    const g = sceneGrid(this);
    const sp = SCORE_PADDING * ui;

    const topLeft = g.cell(0, 0);
    const topRight = g.cell(g.cols - 1, 0);
    const topCenterStart = Math.floor(g.cols / 2 - 2);
    const topCenter = g.cell(topCenterStart, 0, { c: 4 });

    if (this.defenseText) {
      this.defenseText.setPosition(topLeft.x + sp, topLeft.y + sp);
    }
    if (this.scoreText) {
      this.scoreText.setPosition(topRight.right - sp, topLeft.y + sp);
    }
    if (this.ammoText) {
      this.ammoText.setPosition(topRight.right - sp, topLeft.y + sp + AMMO_Y_OFFSET * ui);
    }
    if (this.waveText) {
      this.waveText.setPosition(topCenter.cx, topLeft.y + WAVE_TEXT_Y * ui);
    }
    if (this.waveBarBackground && this.waveBarFill) {
      const waveBarY = topLeft.y + (WAVE_TEXT_Y + WAVE_BAR_Y_OFFSET) * ui;
      const waveBarW = this.waveBarMaxWidth;
      this.waveBarBackground.setPosition(topCenter.cx, waveBarY);
      this.waveBarFill.setPosition(topCenter.cx + waveBarW / 2, waveBarY);
    }

    if (this.playerHpIcons.length > 0) {
      const iconX0 = PLAYER_HP_ICON_X * ui;
      const iconY = PLAYER_HP_ICON_Y * ui;
      const iconGap = PLAYER_HP_ICON_GAP * ui;
      this.playerHpIcons.forEach((icon, i) => {
        icon.setPosition(iconX0 + i * iconGap, iconY);
      });
    }

    if (this.reloadButton && this.reloadIcon) {
      const reloadX = RELOAD_BTN_LEFT_OFFSET * ui;
      const reloadY = RELOAD_BTN_TOP_OFFSET * ui;
      this.reloadButton.setPosition(reloadX, reloadY);
      this.reloadIcon.setPosition(reloadX, reloadY);
    }

    if (this.bossHpBarBg && this.bossHpBarFill && this.bossHpBarBorder) {
      const bossCenter = g.cell(3, 0, { c: 6 });
      const bossX = bossCenter.cx;
      // bossY 30→50: при bossY=30 bossNameText (origin 0.5, ~20px шрифт)
      // ушёл на y=30-24-10 ≈ −4 и срезался верхним краем canvas.
      // 50 — bar центр ниже, name под ним умещается полностью.
      const bossY = 50 * ui;
      const barW = this.bossHpBarMaxWidth;
      this.bossHpBarBg.setPosition(bossX, bossY);
      this.bossHpBarFill.setPosition(bossX - barW / 2, bossY);
      this.bossHpBarBorder.setPosition(bossX, bossY);
      const nameOffset = 24 * ui;
      this.bossNameText?.setPosition(bossX, bossY - nameOffset);
      this.bossPhaseText?.setPosition(bossX, bossY + nameOffset);
    }

    if (this.weaponSlotsUI.length > 0) {
      const slotSize = WEAPON_SLOT_SIZE * ui;
      const slotGap = WEAPON_SLOT_GAP * ui;
      const slotPadding = WEAPON_SLOTS_BOTTOM_PADDING * ui;
      const keyOffsetX = 6 * ui;
      const keyOffsetY = 4 * ui;
      const priceOffsetY = 8 * ui;
      const n = this.weaponSlotsUI.length;
      const totalWidth = n * slotSize + (n - 1) * slotGap;
      const startX = (this.scale.width - totalWidth) / 2 + slotSize / 2;
      const slotY = this.scale.height - slotPadding - slotSize / 2;
      for (let i = 0; i < n; i++) {
        const s = this.weaponSlotsUI[i];
        const x = startX + i * (slotSize + slotGap);
        s.bg.setPosition(x, slotY);
        s.keyText.setPosition(x - slotSize / 2 + keyOffsetX, slotY - slotSize / 2 + keyOffsetY);
        s.priceText?.setPosition(x, slotY - slotSize / 2 - priceOffsetY);
        s.icon?.setPosition(x, slotY);
      }
    }

    this.drawHudPlates();
  }

  // Перерисовывает 3 полупрозрачные плашки под кластерами HUD-текстов. Под
  // каждым кластером — отдельный rounded-rect, размер = bounding box текстов
  // + padding. Цель — fix читаемости на светлом поле боя, без затемнения
  // самого поля. Вызывается в конце relayoutHUD() (когда позиции уже стоят).
  private drawHudPlates(): void {
    if (!this.hudPlates) return;
    this.hudPlates.clear();
    this.hudPlates.fillStyle(0x000000, HUD_PLATE_ALPHA);
    const pad = HUD_PLATE_PADDING;
    const r = HUD_PLATE_RADIUS;

    // 1. Защита (top-left, origin (0, 0)).
    if (this.defenseText) {
      const t = this.defenseText;
      this.hudPlates.fillRoundedRect(t.x - pad, t.y - pad, t.width + 2 * pad, t.height + 2 * pad, r);
    }

    // 2. Wave-баннер (top-center: текст + bar). Текст origin (0.5, 0.5),
    // bar origin (0.5, 0.5). bbox = объединение их display rectangles.
    // Не рисуем во время boss/wave-event — wave UI там скрыт через
    // hideRegularWaveUI, и пустая плашка осталась бы под HP-баром.
    if (
      this.waveText &&
      this.waveBarBackground &&
      this.waveText.visible &&
      this.waveBarBackground.visible
    ) {
      const wt = this.waveText;
      const wb = this.waveBarBackground;
      const left = Math.min(wt.x - wt.width / 2, wb.x - wb.width / 2);
      const right = Math.max(wt.x + wt.width / 2, wb.x + wb.width / 2);
      const top = wt.y - wt.height / 2;
      const bottom = wb.y + wb.height / 2;
      this.hudPlates.fillRoundedRect(left - pad, top - pad, right - left + 2 * pad, bottom - top + 2 * pad, r);
    }

    // 3. Деньги + патроны (top-right, origin (1, 0)). bbox от score-right
    // до max(score, ammo)-width влево.
    if (this.scoreText && this.ammoText) {
      const st = this.scoreText;
      const at = this.ammoText;
      const right = st.x;
      const width = Math.max(st.width, at.width);
      const left = right - width;
      const top = st.y;
      const bottom = at.y + at.height;
      this.hudPlates.fillRoundedRect(left - pad, top - pad, width + 2 * pad, bottom - top + 2 * pad, r);
    }
  }

  private createBossHpBar(boss: Boss): void {
    // Boss HP bar — ширина/высота/шрифт × uiScale. Позиции — в relayoutHUD().
    const ui = getUiScale(this);
    const barWidth = 600 * ui;
    const barHeight = 24 * ui;
    const nameFontSize = `${Math.max(11, Math.round(20 * ui))}px`;
    const phaseFontSize = `${Math.max(11, Math.round(16 * ui))}px`;
    this.bossHpBarMaxWidth = barWidth;

    this.bossHpBarBg = this.add
      .rectangle(0, 0, barWidth, barHeight, 0x222222)
      .setOrigin(0.5)
      .setScrollFactor(0)
      .setDepth(120);

    this.bossHpBarFill = this.add
      .rectangle(0, 0, barWidth, barHeight, 0xcc2222)
      .setOrigin(0, 0.5)
      .setScrollFactor(0)
      .setDepth(121);

    this.bossHpBarBorder = this.add
      .rectangle(0, 0, barWidth, barHeight)
      .setStrokeStyle(2, 0xffffff)
      .setOrigin(0.5)
      .setScrollFactor(0)
      .setDepth(122);

    this.bossNameText = this.add
      .text(0, 0, boss.config.displayName, {
        fontFamily: 'Arial',
        fontSize: nameFontSize,
        color: HEX.white,
        fontStyle: 'bold',
        ...HUD_TEXT_OUTLINE,
      })
      .setOrigin(0.5)
      .setScrollFactor(0)
      .setDepth(122);

    this.bossPhaseText = this.add
      .text(0, 0, '', {
        fontFamily: 'Arial',
        fontSize: phaseFontSize,
        color: HEX.lightGray,
        fontStyle: 'italic',
        ...HUD_TEXT_OUTLINE,
      })
      .setOrigin(0.5)
      .setScrollFactor(0)
      .setDepth(122)
      // Лейбл «Готовится!/АТАКА!/...» скрыт — визуально читался как
      // wave-prep индикатор и забивал место под HP-баром. Тинт босса/анимация
      // уже передают фазу без текста. updateBossPhaseText продолжает
      // обновлять text — если позже понадобится вернуть, убрать setVisible.
      .setVisible(false);

    this.relayoutHUD();
  }

  // Скрывает wave-prep UI обычной волны: текст «Уровень N · Волна M», серая
  // плашка-фон бара и зелёный progress-bar. Дёргается из triggerBossfight
  // и triggerWaveEvent — оба сценария поглощают весь уровень и потом уходят
  // через returnToMapVictory → scene restart, поэтому ручного showRegularWaveUI
  // не нужно: следующая сессия GameScene стартует с дефолтной видимостью.
  private hideRegularWaveUI(): void {
    this.waveText.setVisible(false);
    this.waveBarBackground.setVisible(false);
    this.waveBarFill.setVisible(false);
  }

  private updateBossHpBar(): void {
    if (!this.bossHpBarFill || !this.currentBoss) return;
    const hpRatio = Math.max(0, this.currentBoss.currentHp / this.currentBoss.maxHp);
    this.bossHpBarFill.width = this.bossHpBarMaxWidth * hpRatio;

    let color = 0xcc2222;
    if (hpRatio < 0.25) color = 0xcccc22;
    else if (hpRatio < 0.5) color = 0xcc7722;
    this.bossHpBarFill.setFillStyle(color);
  }

  private updateBossPhaseText(): void {
    if (!this.bossPhaseText || !this.currentBoss) return;
    const phase = this.currentBoss.getChargingPhase();
    if (!phase) return;
    const texts: Record<string, string> = {
      idle: 'Ожидание...',
      windup: 'Готовится!',
      charge: 'АТАКА!',
      recovery: 'Восстановление...',
    };
    const text = texts[phase] ?? '';
    if (this.bossPhaseText.text !== text) this.bossPhaseText.setText(text);
  }

  private destroyBossHpBar(): void {
    this.bossHpBarBg?.destroy();
    this.bossHpBarFill?.destroy();
    this.bossHpBarBorder?.destroy();
    this.bossNameText?.destroy();
    this.bossPhaseText?.destroy();
    this.bossHpBarBg = undefined;
    this.bossHpBarFill = undefined;
    this.bossHpBarBorder = undefined;
    this.bossNameText = undefined;
    this.bossPhaseText = undefined;
  }

  private showBossIntro(fullText: string): void {
    const introText = this.add
      .text(this.scale.width / 2, this.scale.height / 2 - 50, fullText, {
        fontFamily: 'Arial',
        fontSize: '64px',
        color: '#FFCC00',
        fontStyle: 'bold',
        stroke: '#000000',
        strokeThickness: 4,
      })
      .setOrigin(0.5)
      .setAlpha(0)
      .setScrollFactor(0)
      .setDepth(200);

    this.tweens.add({
      targets: introText,
      alpha: 1,
      duration: 500,
      onComplete: () => {
        this.time.delayedCall(1000, () => {
          this.tweens.add({
            targets: introText,
            alpha: 0,
            duration: 500,
            onComplete: () => introText.destroy(),
          });
        });
      },
    });
  }

  private showBossVictory(fullText: string): void {
    const victoryText = this.add
      .text(this.scale.width / 2, this.scale.height / 2 - 50, fullText, {
        fontFamily: 'Arial',
        fontSize: '72px',
        color: '#33FF33',
        fontStyle: 'bold',
        stroke: '#000000',
        strokeThickness: 4,
      })
      .setOrigin(0.5)
      .setAlpha(0)
      .setScrollFactor(0)
      .setDepth(200);

    this.tweens.add({
      targets: victoryText,
      alpha: 1,
      duration: 500,
      onComplete: () => {
        this.time.delayedCall(1500, () => {
          this.tweens.add({
            targets: victoryText,
            alpha: 0,
            duration: 500,
            onComplete: () => victoryText.destroy(),
          });
        });
      },
    });
  }

  private createPlayerHpDisplay(): void {
    // HP-иконки масштабируются uiScale — на 360 вдвое меньше, не съедают левый верх.
    // Позиции — в relayoutHUD().
    const ui = getUiScale(this);
    const iconSize = PLAYER_HP_ICON_SIZE * ui;
    for (let i = 0; i < this.player.maxBoarHits; i++) {
      const icon = this.add.rectangle(0, 0, iconSize, iconSize, 0x33cc33);
      icon.setStrokeStyle(2, 0xffffff);
      icon.setScrollFactor(0).setDepth(110);
      this.playerHpIcons.push(icon);
    }
    this.relayoutHUD();
  }

  private updatePlayerHpDisplay(): void {
    const hits = this.player.boarHits;
    this.playerHpIcons.forEach((icon, i) => {
      icon.setFillStyle(i < hits ? 0x33cc33 : 0x444444);
    });
  }

  private destroyPlayerHpDisplay(): void {
    this.playerHpIcons.forEach((icon) => icon.destroy());
    this.playerHpIcons = [];
  }

  private handleBossFakeDeath(boss: Boss): void {
    const r = boss.bossConfig.reviveOnce;
    if (!r) return;
    // Ставим guard ДО showBossVictory — следующий тик update() не должен повторно
    // войти сюда или эмитить boss:defeated.
    boss.isInFakeDeath = true;

    this.showBossVictory(r.fakeDeathText);

    this.time.delayedCall(1500, () => {
      if (!this.currentBoss) return;
      this.showBossIntro(r.reviveText);
      this.currentBoss.performRevive();
      this.updateBossHpBar();
      this.cameras.main.shake(400, 0.015);
    });
  }

  private handleInstakillContact(boss: Boss): void {
    if (this.instakillInProgress) return;
    if (this.player.isInvulnerable) return;
    this.instakillInProgress = true;

    // Инстакил-боссы (Лось/Медведь): killedBy = id этого босса.
    this.lastDamageSource = boss.config.id;

    const dragMs = boss.bossConfig.instakillDragMs ?? 400;
    this.player.startBeingDragged();
    this.cameras.main.shake(dragMs, 0.015);

    this.time.delayedCall(dragMs, () => {
      this.player.die();
      this.instakillInProgress = false;
    });
  }

  private handlePlayerDeath(): void {
    if (!this.bossfightActive) return;
    this.bossfightActive = false;

    if (this.currentBoss) {
      const idx = this.enemies.indexOf(this.currentBoss);
      if (idx >= 0) this.enemies.splice(idx, 1);
      this.currentBoss.destroy();
      this.currentBoss = null;
    }

    this.destroyPlayerHpDisplay();
    this.destroyBossHpBar();
    this.clearEnemyProjectiles();
    this.clearZones();

    const gameOverText = this.add.text(
      this.scale.width / 2,
      this.scale.height / 2,
      'GAME OVER',
      { fontSize: '80px', color: '#FF0000', fontStyle: 'bold' },
    );
    gameOverText.setOrigin(0.5).setScrollFactor(0).setDepth(200);

    this.time.delayedCall(3000, () => this.returnToMapDefeat());
  }

  private triggerWaveEvent(waveId: string): void {
    if (this.waveEventActive || this.bossfightActive) return;

    const config = getWaveEventConfig(waveId);

    this.waveEventActive = true;
    this.waveEventConfig = config;
    this.waveEventEnemiesTotal = config.enemyCount;
    this.waveEventEnemiesResolved = 0;
    this.waveEventEnemiesSpawned = 0;

    // Скрываем wave-prep UI — wave-event показывает свой waveProgressBar сверху,
    // регулярный wave bar только дублирует визуал.
    this.hideRegularWaveUI();

    // Прерываем обычные волны (аналогично triggerBossfight).
    for (const enemy of this.enemies) {
      if (!enemy.getIsDying()) enemy.destroy();
    }
    this.enemies = [];
    this.enemiesRemainingInWave = 0;
    this.enemiesSpawnedInWave = 0;
    this.spawnTimer?.destroy();

    this.showBossIntro(config.introText);
    this.createWaveProgressDisplay();
    this.updateWaveProgressDisplay();

    this.waveEventSpawnTimer = this.time.addEvent({
      delay: config.spawnIntervalMs,
      repeat: config.enemyCount - 1,
      callback: () => this.spawnWaveEventEnemy(),
    });
  }

  private spawnWaveEventEnemy(): void {
    if (!this.waveEventConfig) return;
    const enemyConfig = ENEMY_CONFIGS[this.waveEventConfig.enemyId];
    if (!enemyConfig) return;

    // Wave events (Лисы/Волки/Кабанчики/Олени): тоже от ЛЕВОГО КРАЯ CANVAS
    // (как обычные волны), полный path через лес → поле → кукуруза.
    const spawnX = ENEMY_SPAWN_X_OFFSET;
    // Y из рядов 2..16 fieldGrid'а 40×20 (см. getEnemySpawnY*).
    const spawnYMin = this.getEnemySpawnYMin();
    const spawnYMax = this.getEnemySpawnYMax();
    const spawnY = spawnYMin + Math.random() * Math.max(0, spawnYMax - spawnYMin);

    const enemy = new Enemy(this, spawnX, spawnY, enemyConfig);
    enemy.isWaveEventEnemy = true;
    this.enemies.push(enemy);
    this.waveEventEnemiesSpawned += 1;
  }

  private onWaveEventComplete(): void {
    if (!this.waveEventConfig) return;

    this.showBossVictory(this.waveEventConfig.victoryText);

    this.waveEventActive = false;
    this.waveEventConfig = null;
    this.waveEventSpawnTimer?.destroy();
    this.waveEventSpawnTimer = undefined;
    this.destroyWaveProgressDisplay();

    // Wave event = весь уровень. Сразу переходим к следующему.
    this.onLevelComplete();
  }

  private createWaveProgressDisplay(): void {
    // Wave-event progress (Лисы/Волки/Кабанчики/Олени) — позиция/размер × uiScale.
    const ui = getUiScale(this);
    const barWidth = 600 * ui;
    const barX = this.scale.width / 2;
    // barY 30→50: симметрично boss HP-bar. При barY=30 заголовок
    // («Волна Оленей — осталось: N», origin 0.5) уходит на y=30-24-10 ≈ −4
    // и срезается верхним краем canvas.
    const barY = 50 * ui;
    const barHeight = 20 * ui;
    const textOffset = 24 * ui;
    const fontSize = `${Math.max(11, Math.round(20 * ui))}px`;
    this.waveProgressBarMaxWidth = barWidth;

    this.waveProgressText = this.add
      .text(barX, barY - textOffset, '', {
        fontFamily: 'Arial',
        fontSize,
        color: HEX.white,
        fontStyle: 'bold',
      })
      .setOrigin(0.5)
      .setScrollFactor(0)
      .setDepth(122);

    this.waveProgressBarBg = this.add
      .rectangle(barX, barY, barWidth, barHeight, 0x222222)
      .setOrigin(0.5)
      .setScrollFactor(0)
      .setDepth(120);

    this.waveProgressBarFill = this.add
      .rectangle(barX - barWidth / 2, barY, 0, barHeight, 0xcc6622)
      .setOrigin(0, 0.5)
      .setScrollFactor(0)
      .setDepth(121);
  }

  private updateWaveProgressDisplay(): void {
    if (!this.waveProgressText || !this.waveEventConfig) return;
    const resolved = this.waveEventEnemiesResolved;
    const total = this.waveEventEnemiesTotal;
    const remaining = Math.max(0, total - resolved);
    this.waveProgressText.setText(`${this.waveEventConfig.displayName} — осталось: ${remaining}`);
    if (this.waveProgressBarFill) {
      const ratio = total > 0 ? resolved / total : 0;
      this.waveProgressBarFill.width = this.waveProgressBarMaxWidth * ratio;
    }
  }

  private destroyWaveProgressDisplay(): void {
    this.waveProgressText?.destroy();
    this.waveProgressBarBg?.destroy();
    this.waveProgressBarFill?.destroy();
    this.waveProgressText = undefined;
    this.waveProgressBarBg = undefined;
    this.waveProgressBarFill = undefined;
  }

  private startLevel(level: number): void {
    this.currentLevel = level;
    this.currentWave = 0;
    this.wavesInCurrentLevel = 0;
    this.currentWaveEntries = [];
    this.currentWaveEntryIndex = 0;

    // Аналитика: точка входа в уровень (включая boss/wave_event). Внутри уровня
    // startNextWaveInLevel НЕ эмитит — только смена волн.
    this.levelStartedAt = this.time.now;
    this.levelResolved = false;
    this.lastDamageSource = null;
    const startPayload: AnalyticsLevelStartPayload = { level };
    this.events.emit('analytics:level-start', startPayload);

    // Открыть транзакцию: снапшот ПЕРЕД боем. Победа → коммит + save. Проигрыш → restore.
    // Снапшот делается на КАЖДОМ уровне (включая Continue-цепочки), чтобы каждый уровень
    // имел свою точку отката (после L3 win + commit → snapshot для L4 = post-L3 состояние).
    mapProgress.takeSnapshot();

    const override = getLevelOverride(level);
    if (override) {
      if (override.type === 'charging_boss' && override.bossId) {
        this.triggerBossfight(override.bossId);
      } else if (override.type === 'wave_event' && override.bossId) {
        this.triggerWaveEvent(override.bossId);
      }
      return;
    }

    // Обычный уровень — процедурные волны.
    this.wavesInCurrentLevel = getWavesForLevel(level);
    this.startNextWaveInLevel();
  }

  // Идемпотентный эмит analytics:level-complete: срабатывает при первом вызове,
  // блокируется флагом levelResolved. Вызывается из onLevelComplete (все обычные
  // + промежуточные боссовые пути) и из showFinalVictory (L50, миную onLevelComplete).
  private emitAnalyticsLevelComplete(): void {
    if (this.levelResolved) return;
    this.levelResolved = true;
    const payload: AnalyticsLevelCompletePayload = {
      level: this.currentLevel,
      durationMs: Math.round(this.time.now - this.levelStartedAt),
    };
    this.events.emit('analytics:level-complete', payload);
  }

  // Симметричный failed-эмит. Читает lastDamageSource (устанавливается в моменты
  // фактического урона игроку / прорыва защиты до нуля); null → 'unknown'.
  // Вызывается из returnToMapDefeat — единой точки поражения.
  private emitAnalyticsLevelFailed(): void {
    if (this.levelResolved) return;
    this.levelResolved = true;
    const payload: AnalyticsLevelFailedPayload = {
      level: this.currentLevel,
      killedBy: this.lastDamageSource ?? 'unknown',
      wave: this.currentWave,
    };
    this.events.emit('analytics:level-failed', payload);
  }

  private onLevelComplete(): void {
    // Эмитим до MAX_LEVEL-гарда, чтобы analytics:level-complete срабатывал и для
    // не-финальных уровней в обычной ветке (L50 идёт через showFinalVictory —
    // его эмит там же).
    this.emitAnalyticsLevelComplete();

    // Уровень 50 — финальный босс, его смерть сама показывает showFinalVictory.
    // onLevelComplete для него не должен ничего делать.
    if (this.currentLevel >= MAX_LEVEL) return;

    // Фиксируем прогресс/деньги ОДИН раз здесь. Дальше — выбор: продолжить или на карту.
    this.commitLevelProgress();

    // Боссовый уровень (главный 10/20/30/40/50 или мини 15/25/35/45) — насильно на карту.
    // Обычный — экран выбора.
    if (getLevelOverride(this.currentLevel) !== null) {
      this.returnToMapVictory();
    } else {
      this.showLevelClearChoice();
    }
  }

  // Идемпотентно: при повторном вызове wasReplay=true → addMoney пропускается,
  // earnedThisLevel уже занулён. Безопасно дёргать дважды (onLevelComplete → returnToMapVictory).
  // Закрывает транзакцию (clearSnapshot) и персистит на диск — это «фиксация» победы.
  private commitLevelProgress(): void {
    const wasReplay = mapProgress.isLevelCompleted(this.currentLevel);
    if (!wasReplay) {
      mapProgress.addMoney(this.earnedThisLevel);
    }
    mapProgress.completeLevel(this.currentLevel);
    this.earnedThisLevel = 0;
    mapProgress.clearSnapshot();
    // Fire-and-forget — ошибки сохранения логируются внутри saveToProvider, геймплей не блокируем.
    void mapProgress.saveToProvider(getSaveProvider());
  }

  private returnToMapVictory(): void {
    // Идемпотентно — если уже закоммитили через onLevelComplete, повторный вызов no-op.
    // Защита для прямых вызовов из showFinalVictory / выбора игрока 'На карту'.
    this.commitLevelProgress();
    MusicManager.getInstance().stop(this);
    this.scene.start('MapScene', { lastResult: 'victory', level: this.currentLevel });
  }

  private returnToMapDefeat(): void {
    // Единая точка поражения (handlePlayerDeath / gameOver → returnToMapDefeat)
    // — эмитим analytics:level-failed здесь до scene.start, чтобы событие
    // успело улететь до shutdown'а сцены (aналитический subscriber отпишется
    // по SHUTDOWN, но синхронный emit + синхронная обработка успевают).
    this.emitAnalyticsLevelFailed();

    // Откат транзакции: всё что произошло за бой — деньги (заработок и траты), купленное
    // оружие, разблокировки если успел — пропадает. Состояние = takeSnapshot из startLevel.
    // В CloudStorage НЕ пишем — там уже последняя успешная фиксация.
    mapProgress.restoreFromSnapshot();
    mapProgress.clearSnapshot();
    this.earnedThisLevel = 0;
    MusicManager.getInstance().stop(this);
    this.scene.start('MapScene', { lastResult: 'defeat', level: this.currentLevel });
  }

  private showLevelClearChoice(): void {
    // Заморозить таймеры — никаких "вылета" спавнов под overlay'ем.
    this.spawnTimer?.destroy();
    this.breakTimer?.destroy();
    this.waveEventSpawnTimer?.destroy();
    this.waveBarBackground.setVisible(false);
    this.waveBarFill.setVisible(false);
    this.waveText.setAlpha(0);

    const cx = this.scale.width / 2;
    const cy = this.scale.height / 2;

    const overlay = this.add
      .rectangle(cx, cy, this.scale.width, this.scale.height, 0x000000, 0.6)
      .setScrollFactor(0)
      .setDepth(250)
      .setInteractive();

    const title = this.add
      .text(cx, cy - 100, `УРОВЕНЬ ${this.currentLevel} ПРОЙДЕН`, {
        fontFamily: 'Arial',
        fontSize: '48px',
        color: HEX.white,
        fontStyle: 'bold',
      })
      .setOrigin(0.5)
      .setScrollFactor(0)
      .setDepth(251);

    const makeBtn = (y: number, label: string): Phaser.GameObjects.Text =>
      this.add
        .text(cx, y, label, {
          fontFamily: 'Arial',
          fontSize: '32px',
          color: HEX.yellow,
          backgroundColor: '#333333',
          padding: { left: 24, right: 24, top: 12, bottom: 12 },
        })
        .setOrigin(0.5)
        .setScrollFactor(0)
        .setDepth(251)
        .setInteractive({ useHandCursor: true });

    const continueBtn = makeBtn(cy + 10, 'ПРОДОЛЖИТЬ');
    const mapBtn = makeBtn(cy + 90, 'НА КАРТУ');

    const cleanup = (): void => {
      overlay.destroy();
      title.destroy();
      continueBtn.destroy();
      mapBtn.destroy();
    };

    // stopPropagation: иначе тап по кнопке (особенно если она в правой половине после
    // ресайза) параллельно активирует joystick / стрельбу через global input listeners.
    continueBtn.on(
      'pointerdown',
      (
        _pointer: Phaser.Input.Pointer,
        _x: number,
        _y: number,
        event: Phaser.Types.Input.EventData,
      ) => {
        event.stopPropagation();
        cleanup();
        // Стряхнуть остаточные пули/гранаты/проджектайлы и т.п. перед следующим уровнем.
        this.cleanupCurrentState();
        this.startLevel(this.currentLevel + 1);
      },
    );

    mapBtn.on(
      'pointerdown',
      (
        _pointer: Phaser.Input.Pointer,
        _x: number,
        _y: number,
        event: Phaser.Types.Input.EventData,
      ) => {
        event.stopPropagation();
        cleanup();
        this.returnToMapVictory();
      },
    );
  }

  private cleanupCurrentState(): void {
    this.spawnTimer?.destroy();
    this.breakTimer?.destroy();
    this.waveEventSpawnTimer?.destroy();

    for (const enemy of this.enemies) {
      if (!enemy.getIsDying()) enemy.destroy();
    }
    this.enemies = [];

    for (const b of this.bullets) b.destroy();
    this.bullets = [];
    for (const g of this.grenades) g.destroy();
    this.grenades = [];

    this.clearEnemyProjectiles();
    this.clearZones();

    this.bossfightActive = false;
    this.currentBoss = null;
    this.instakillInProgress = false;
    this.destroyBossHpBar();
    this.destroyPlayerHpDisplay();

    this.waveEventActive = false;
    this.waveEventConfig = null;
    this.waveEventEnemiesTotal = 0;
    this.waveEventEnemiesResolved = 0;
    this.waveEventEnemiesSpawned = 0;
    this.destroyWaveProgressDisplay();

    this.player.restoreBoarHits();
    this.isInBreak = false;
  }

  private startNextWaveInLevel(): void {
    if (this.currentWave >= this.wavesInCurrentLevel) {
      this.onLevelComplete();
      return;
    }

    this.sound.play('wave_start', { volume: VOLUME_WAVE_START });
    this.events.emit('fx:wave-start');
    this.currentWave += 1;
    this.isInBreak = false;

    this.currentWaveEntries = generateWave(this.currentLevel);
    this.currentWaveEntryIndex = 0;
    // Подсчёт ОТДЕЛЬНЫХ врагов для wave bar (mouse entry = группа из 4).
    let total = 0;
    for (const id of this.currentWaveEntries) {
      total += id === 'mouse' ? WG_MOUSE_GROUP_SIZE : 1;
    }
    this.enemiesInCurrentWave = total;
    this.enemiesRemainingInWave = total;
    this.enemiesSpawnedInWave = 0;

    this.waveBarBackground.setVisible(true);
    this.waveBarFill.setVisible(true);
    this.waveBarFill.width = this.waveBarMaxWidth;
    this.waveBarFill.fillColor = COLORS.green;

    // Интервал спавна — функция от уровня (не wave-in-level), калибровка W11.
    this.currentSpawnInterval = Math.max(
      MIN_SPAWN_INTERVAL,
      STARTING_SPAWN_INTERVAL - (this.currentLevel - 1) * 30,
    );
    this.waveText.setAlpha(1);
    this.waveText.setColor(HEX.white);
    this.waveText.setText(`Уровень ${this.currentLevel} · Волна ${this.currentWave}`);

    this.tweens.add({
      targets: this.waveText,
      alpha: 0,
      delay: WAVE_TEXT_FADE_DELAY_MS,
      duration: WAVE_TEXT_FADE_DURATION_MS,
      onComplete: () => this.waveText.setAlpha(1),
    });

    this.spawnTimer?.destroy();
    this.spawnTimer = this.time.addEvent({
      delay: this.currentSpawnInterval,
      loop: true,
      callback: this.spawnWave,
      callbackScope: this,
    });
  }

  private onEnemyResolved(enemy: Enemy): void {
    // Бонусные spawn-on-death дети (кенгурёнок) не входят в счётчик волны.
    if (enemy.isSpawnOnDeathChild) return;
    // Босс — отдельный режим, обрабатывается через onBossDefeated.
    if (enemy instanceof Boss) return;

    // Wave event враги — отдельный счётчик, не идут в обычный wave-counter.
    // Победа = все заспавнены И все resolved (убиты или прорвались).
    if (enemy.isWaveEventEnemy && this.waveEventActive) {
      this.waveEventEnemiesResolved += 1;
      this.updateWaveProgressDisplay();
      if (
        this.waveEventEnemiesResolved >= this.waveEventEnemiesTotal &&
        this.waveEventEnemiesSpawned >= this.waveEventEnemiesTotal
      ) {
        this.onWaveEventComplete();
      }
      return;
    }
    this.enemiesRemainingInWave--;
    this.updateWaveBar();

    if (this.enemiesRemainingInWave <= 0 && !this.isInBreak) {
      // Дополнительная защита: если ещё не все запланированные entry-spawn'ы ушли в мир
      // (например, getNonOverlappingY вернул null и спавн пропустился) — не закрываем волну.
      if (this.currentWaveEntryIndex < this.currentWaveEntries.length) return;
      if (this.currentWave >= this.wavesInCurrentLevel) {
        this.onLevelComplete();
      } else {
        this.startBreak();
      }
    }
  }

  private updateWaveBar(): void {
    if (this.enemiesInCurrentWave === 0) return;

    const remainingRatio = this.enemiesRemainingInWave / this.enemiesInCurrentWave;
    this.waveBarFill.width = this.waveBarMaxWidth * remainingRatio;

    if (remainingRatio > WAVE_BAR_RATIO_YELLOW) {
      this.waveBarFill.fillColor = COLORS.green;
    } else if (remainingRatio > WAVE_BAR_RATIO_RED) {
      this.waveBarFill.fillColor = COLORS.yellow;
    } else {
      this.waveBarFill.fillColor = COLORS.red;
    }
  }

  private startBreak(): void {
    this.sound.play('wave_end', { volume: VOLUME_WAVE_END });
    this.events.emit('fx:wave-end');

    this.waveBarBackground.setVisible(false);
    this.waveBarFill.setVisible(false);

    this.isInBreak = true;
    this.spawnTimer?.destroy();

    const countdownStart = Math.floor(WAVE_BREAK_MS / 1000);
    this.waveText.setAlpha(1);
    this.waveText.setColor(HEX.yellow);
    const labelNext = `Уровень ${this.currentLevel} · Волна ${this.currentWave + 1}`;
    this.waveText.setText(`${labelNext} через ${countdownStart}`);

    let remaining = countdownStart;
    const updateInterval = this.time.addEvent({
      delay: COUNTDOWN_TICK_MS,
      loop: true,
      callback: () => {
        remaining--;
        if (remaining > 0) {
          this.waveText.setText(`${labelNext} через ${remaining}`);
        }
      },
    });

    this.breakTimer?.destroy();
    this.breakTimer = this.time.delayedCall(WAVE_BREAK_MS, () => {
      updateInterval.destroy();
      this.startNextWaveInLevel();
    });
  }

  // Y-границы спавна врагов = движение врагов (см. constants/field.ts
  // getEnemyMovement*Y). Единый источник — DRY: если позже сдвинем зону
  // геймплея по Y, правится в одном месте.
  private getEnemySpawnYMin(): number {
    return getEnemyMovementTopY(this);
  }

  private getEnemySpawnYMax(): number {
    return getEnemyMovementBottomY(this);
  }

  // Legacy-имя для совместимости с тремя callsite'ами (mouse group, wave event,
  // getNonOverlappingY) — просто алиас на getEnemySpawnYMax.
  private getReachableSpawnYMax(): number {
    return this.getEnemySpawnYMax();
  }

  private getNonOverlappingY(usedYs: number[], maxAttempts = 20): number | null {
    const spawnYMin = this.getEnemySpawnYMin();
    const spawnYMax = this.getEnemySpawnYMax();
    for (let i = 0; i < maxAttempts; i++) {
      const y = Phaser.Math.Between(spawnYMin, spawnYMax);
      const tooClose = usedYs.some((usedY) => Math.abs(usedY - y) < SPAWN_Y_GAP);
      if (!tooClose) return y;
    }
    return null;
  }

  private formatScore(): string {
    return `Очки: ${this.score}`;
  }

  // Начисление за врага: +score и +earnedThisLevel. Earnings коммитятся в mapProgress.money
  // только при первой победе уровня (в returnToMapVictory). Replay/проигрыш — сгорают.
  private awardScore(amount: number): void {
    this.score += amount;
    this.earnedThisLevel += amount;
  }

  private createWeaponSlotsHUD(): void {
    this.weaponSlotsUI = [];
    const slotConfigs: { slot: number; createFn: () => Weapon; key: string }[] = [
      { slot: 0, createFn: createRevolver, key: '1' },
      { slot: 1, createFn: createDualRevolver, key: '2' },
      { slot: 2, createFn: createAssaultRifle, key: '3' },
      { slot: 3, createFn: createShotgun, key: '4' },
      { slot: 4, createFn: createUzi, key: '5' },
      { slot: 5, createFn: createSniper, key: '6' },
      { slot: 6, createFn: createMachineGun, key: '7' },
      { slot: 7, createFn: createGrenadeLauncher, key: '8' },
      { slot: 8, createFn: createLaser, key: '9' },
    ];

    // Адаптивный uiScale от высоты экрана. На 720 = 1.0 (как было), на 360 = 0.5 —
    // слоты, gap, padding, шрифты пропорционально меньше, не съедают треть экрана.
    // Координаты — в relayoutHUD; здесь только размеры/шрифты/иконки.
    const uiScale = getUiScale(this);
    const slotSize = WEAPON_SLOT_SIZE * uiScale;
    const slotBorderWidth = Math.max(1, WEAPON_SLOT_BORDER_WIDTH * uiScale);
    const slotFontSize = Math.max(8, Math.round(14 * uiScale));

    for (let i = 0; i < slotConfigs.length; i++) {
      const cfg = slotConfigs[i];

      const bg = this.add.rectangle(0, 0, slotSize, slotSize, SLOT_COLOR_LOCKED);
      bg.setStrokeStyle(slotBorderWidth, SLOT_BORDER_DEFAULT);
      bg.setDepth(50);
      bg.setScrollFactor(0);
      bg.setInteractive();

      const keyText = this.add
        .text(0, 0, cfg.key, {
          fontFamily: 'Arial',
          fontSize: `${slotFontSize}px`,
          color: HEX.white,
          fontStyle: 'bold',
        })
        .setDepth(51)
        .setScrollFactor(0);

      const weaponConfig = cfg.createFn().config;
      const price = weaponConfig.price;
      let priceText: Phaser.GameObjects.Text | undefined;
      if (price > 0) {
        priceText = this.add
          .text(0, 0, `${price}`, {
            fontFamily: 'Arial',
            fontSize: `${slotFontSize}px`,
            color: HEX.lightGray,
            fontStyle: 'bold',
          })
          .setOrigin(0.5, 1)
          .setDepth(51)
          .setScrollFactor(0);
      }

      // Иконка оружия в слот, если для id загружена текстура weapon_<id>.
      // Depth 50.5 — между bg (50) и цифрой/ценой (51), не перекрывает HUD-текст.
      // Вписываем по меньшей стороне с padding'ом под бордер, сохраняем пропорции,
      // затем масштабируем на ICON_SCALE_BOOST — иконки заметно выходят за квадрат
      // слота, но HUD-цифра/цена сверху на depth 51 остаются читаемыми.
      const iconKey = `weapon_${weaponConfig.id}`;
      let icon: Phaser.GameObjects.Image | undefined;
      if (this.textures.exists(iconKey)) {
        const iconPadding = 8 * uiScale;
        const maxIcon = slotSize - iconPadding * 2;
        const ICON_SCALE_BOOST = 1.35;
        icon = this.add.image(0, 0, iconKey).setDepth(50.5).setScrollFactor(0);
        const tex = icon.texture.getSourceImage() as HTMLImageElement | HTMLCanvasElement;
        const fitScale = Math.min(maxIcon / tex.width, maxIcon / tex.height);
        icon.setScale(fitScale * ICON_SCALE_BOOST);
      }

      bg.on(
        'pointerdown',
        (
          _pointer: Phaser.Input.Pointer,
          _x: number,
          _y: number,
          event: Phaser.Types.Input.EventData,
        ) => {
          this.tryBuyOrSwitchWeapon(cfg.slot, cfg.createFn);
          event.stopPropagation();
        },
      );

      this.weaponSlotsUI.push({
        bg,
        keyText,
        priceText,
        icon,
        slot: cfg.slot,
        createFn: cfg.createFn,
        price,
      });
    }
  }

  private updateWeaponSlotsHUD(): void {
    // Тот же масштаб что в createWeaponSlotsHUD — иначе при apply setStrokeStyle
    // border ширина "прыгнет" обратно к base (3) поверх масштабированного слота.
    const slotBorderWidth = Math.max(1, WEAPON_SLOT_BORDER_WIDTH * getUiScale(this));
    const currentSlot = this.player.inventory.getCurrentSlot();
    for (const slotUI of this.weaponSlotsUI) {
      const isOwned = this.player.inventory.isSlotOccupied(slotUI.slot);
      const isActive = currentSlot === slotUI.slot;
      const weaponId = slotUI.createFn().config.id;
      const isUnlocked = mapProgress.isWeaponUnlocked(weaponId);

      if (isOwned) {
        slotUI.bg.setFillStyle(SLOT_COLOR_OWNED);
        slotUI.bg.setStrokeStyle(
          slotBorderWidth,
          isActive ? SLOT_BORDER_ACTIVE : SLOT_BORDER_DEFAULT,
        );
        if (slotUI.priceText) slotUI.priceText.setVisible(false);
      } else if (!isUnlocked) {
        // Прогрессионно заблокировано — босс ещё не побеждён. Тёмный фон, цена скрыта.
        slotUI.bg.setFillStyle(SLOT_COLOR_PROGRESSION_LOCKED);
        slotUI.bg.setStrokeStyle(slotBorderWidth, SLOT_BORDER_DEFAULT);
        if (slotUI.priceText) slotUI.priceText.setVisible(false);
      } else {
        const canAfford = this.score >= slotUI.price;
        if (canAfford) {
          slotUI.bg.setFillStyle(SLOT_COLOR_AVAILABLE);
          slotUI.bg.setStrokeStyle(slotBorderWidth, SLOT_BORDER_AVAILABLE);
        } else {
          slotUI.bg.setFillStyle(SLOT_COLOR_LOCKED);
          slotUI.bg.setStrokeStyle(slotBorderWidth, SLOT_BORDER_DEFAULT);
        }
        if (slotUI.priceText) {
          slotUI.priceText.setVisible(true);
          slotUI.priceText.setColor(canAfford ? HEX.yellow : HEX.red);
        }
      }
    }
  }

  // На старте сцены: добавить в inventory все купленные ранее оружия (кроме slot 0 —
  // он стартовый и уже инициализирован Inventory). Источник истины — mapProgress.ownedWeapons.
  private restoreOwnedWeapons(): void {
    for (const slotUI of this.weaponSlotsUI) {
      if (slotUI.slot === 0) continue; // revolver — уже в руках
      const w = slotUI.createFn();
      if (mapProgress.isWeaponOwned(w.config.id) && !this.player.inventory.isSlotOccupied(slotUI.slot)) {
        this.player.inventory.addWeaponAtSlot(w, slotUI.slot);
      }
    }
  }

  private tryBuyOrSwitchWeapon(slot: number, createFn: () => Weapon): void {
    if (this.player.inventory.isSlotOccupied(slot)) {
      this.player.inventory.switchToSlot(slot);
      return;
    }

    const weapon = createFn();
    // Заблокировано прогрессией — нельзя купить (нужно сначала пройти соотв. босса).
    if (!mapProgress.isWeaponUnlocked(weapon.config.id)) return;
    if (this.score < weapon.config.price) return;

    this.score -= weapon.config.price;
    // Коммит траты сразу в mapProgress — покупка персистентна. Invariant сохранён
    // (this.score - earnedThisLevel === mapProgress.money), потому что earnedThisLevel не трогаем.
    mapProgress.spendMoney(weapon.config.price);
    mapProgress.addOwnedWeapon(weapon.config.id);
    this.scoreText.setText(this.formatScore());
    this.player.inventory.addWeaponAtSlot(weapon, slot);
    this.player.inventory.switchToSlot(slot);
    // Payload = id для будущей аналитики (какое оружие покупают чаще всего).
    // Haptic-подписчик payload игнорит — интересна только сама покупка.
    this.events.emit('fx:weapon-purchased', { id: weapon.config.id });
  }

  private updateAmmoDisplay(): void {
    const weapon = this.player.currentWeapon;

    if (this.player.isShootingBlocked) {
      this.ammoText.setText('ОГЛУШЁН');
      this.ammoText.setColor(HEX.red);
      return;
    }

    if (weapon.getIsReloading()) {
      this.ammoText.setText('ПЕРЕЗАРЯДКА');
      this.ammoText.setColor(HEX.yellow);
      return;
    }

    if (weapon.isBeam()) {
      const ratio = weapon.getEnergyRatio();
      const pct = Math.round(ratio * 100);
      this.ammoText.setText(`⚡ ${pct}%`);
      if (ratio > AMMO_THRESHOLD_HIGH) {
        this.ammoText.setColor(HEX.green);
      } else if (ratio > AMMO_THRESHOLD_LOW) {
        this.ammoText.setColor(HEX.yellow);
      } else {
        this.ammoText.setColor(HEX.red);
      }
      return;
    }

    if (weapon.hasInfiniteAmmo()) {
      this.ammoText.setText('∞');
      this.ammoText.setColor(HEX.green);
      return;
    }

    const current = weapon.getAmmo();
    const max = weapon.getMaxAmmo();
    this.ammoText.setText(`${current}/${max}`);

    const ratio = current / max;
    if (ratio > AMMO_THRESHOLD_HIGH) {
      this.ammoText.setColor(HEX.green);
    } else if (ratio > AMMO_THRESHOLD_LOW) {
      this.ammoText.setColor(HEX.yellow);
    } else {
      this.ammoText.setColor(HEX.red);
    }
  }

  private formatDefense(): string {
    return `Защита: ${this.defense}%`;
  }

  private updateDefenseDisplay(): void {
    this.defenseText.setText(this.formatDefense());
    if (this.defense > DEFENSE_THRESHOLD_HEALTHY) {
      this.defenseText.setColor(HEX.green);
    } else if (this.defense > DEFENSE_THRESHOLD_WARNING) {
      this.defenseText.setColor(HEX.yellow);
      if (!this.playedDefenseLowSound) {
        this.sound.play('defense_low', { volume: VOLUME_DEFENSE_LOW });
        this.events.emit('fx:defense-warning');
        this.playedDefenseLowSound = true;
      }
    } else {
      this.defenseText.setColor(HEX.red);
      if (!this.playedDefenseCriticalSound) {
        this.sound.play('defense_critical', { volume: VOLUME_DEFENSE_CRITICAL });
        this.events.emit('fx:defense-warning');
        this.playedDefenseCriticalSound = true;
      }
    }
  }

  private gameOver(): void {
    this.sound.play('game_over', { volume: VOLUME_GAME_OVER });
    this.events.emit('fx:game-over');
    MusicManager.getInstance().stop(this);

    this.waveBarBackground.setVisible(false);
    this.waveBarFill.setVisible(false);

    this.spawnTimer?.destroy();
    this.breakTimer?.destroy();
    this.waveEventSpawnTimer?.destroy();
    this.clearEnemyProjectiles();
    this.clearZones();
    this.enemies.forEach((e) => e.body.setVelocity(0));
    this.bullets.forEach((b) => b.body?.setVelocity(0));
    // Мягкий проигрыш — возврат на карту, не на GameOverScene. Точка не отмечена пройденной,
    // игрок может зайти снова. GameOverScene остаётся в коде для будущего (например хард-моде).
    this.time.delayedCall(GAME_OVER_DELAY_MS, () => this.returnToMapDefeat());
  }
}
